import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js/pure';
import type { StripeElementsOptions } from '@stripe/stripe-js';
import { useLocation, useParams } from 'react-router-dom';
import { useScreenSize } from '../../contexts/ScreenSizeContext';
import HeaderCard from '../../components/HeaderCard/HeaderCard';
import { HeaderCardVariationTypes } from '../../components/HeaderCard/HeaderCardElements';
import ScrollableContainer from '../../components/ScrollableContainer/ScrollableContainer';
import TimePicker, { PeriodKey } from './components/TimePicker';
import ServiceCard from './components/ServiceCard';
import {
  abandonPublicDeposit,
  createPublicAppointment,
  cancelManagedPublicAppointment,
  CreatePublicAppointmentResponse,
  finalizePublicDeposit,
  getManagedPublicAppointment,
  getPublicBookingAvailabilityBatch,
  getPublicBookingPage,
  PublicBookingService,
  PublicBookingPageResponse,
  PublicManageAppointmentResponse,
  rescheduleManagedPublicAppointment,
} from '../../services/publicBookingApi';
import { DepositType, PlanFeature } from '../../dataModels/models';
import {
  resolveColorScheme,
} from '../../styling/ColorSchemes';
import { FONT_THEME } from '../../styling/FontSchemes';
import { businessHasFeature } from '../../utils/planFeatures';
import { preloadFontScheme } from '../../contexts/StyleContext';
import { getBusinessCustomerContactDetails } from '../../utils/customerContactPreference';
import PageSeo from '../../components/PageSeo/PageSeo';
import { PUBLIC_SITE_ORIGIN } from '../../constants/ApplicationConstants';
import { isPublicProductionHost } from '../../utils/runtimeHost';
import { readUrlStateParam } from '../../utils/urlState';
import { buildBookingPageUrl, getBookingSubdomainSlug } from '../../utils/bookingSubdomain';

type Step = 'services' | 'time' | 'overview' | 'deposit' | 'confirmed';

type DepositReturnState = {
  appointmentId: string;
  serviceId: string;
  date: string;
  time: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  marketingConsent: boolean;
  smsConsent: boolean;
  clientNotes: string;
};

type BookingPageProps = {
  bookingSlug?: string;
  previewData?: PublicBookingPageResponse;
  previewAvailabilityByServiceId?: Record<string, Record<string, string[]>>;
  previewMode?: boolean;
  requireBookingSubdomainEntitlement?: boolean;
};

const cardBase: React.CSSProperties = {
  background: '#FFFFFF',
  border: '1px solid #D9D9D9',
  borderRadius: '0.9rem',
  padding: '1rem 1.1rem 1.15rem',
  boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.6rem',
  fontFamily: 'inherit',
};

type DepositPaymentFormProps = {
  onSubmitted: (paymentIntentId: string) => Promise<void>;
  isSaving: boolean;
  accentColor: string;
  layoutWidth: number;
  returnUrl: string;
};

const DepositPaymentForm: React.FC<DepositPaymentFormProps> = ({
  onSubmitted,
  isSaving,
  accentColor,
  layoutWidth,
  returnUrl,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const layoutScale = Math.min(1, Math.max(0, (layoutWidth - 280) / 744));
  const scaledRem = (minimum: number, maximum: number) =>
    `${minimum + (maximum - minimum) * layoutScale}rem`;

  const handleSubmit = async () => {
    if (!stripe || !elements) {
      return;
    }

    setIsSubmitting(true);
    setPaymentError('');
    const result = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: returnUrl },
      redirect: 'if_required',
    });

    if (result.error) {
      setPaymentError(result.error.message ?? 'Unable to confirm the deposit payment.');
      setIsSubmitting(false);
      return;
    }

    const paymentIntentId = result.paymentIntent?.id;
    if (!paymentIntentId) {
      setPaymentError('Stripe did not return a payment confirmation.');
      setIsSubmitting(false);
      return;
    }

    try {
      await onSubmitted(paymentIntentId);
    } catch (error) {
      setPaymentError(error instanceof Error ? error.message : 'Unable to finalize the appointment.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{ display: 'grid', gap: scaledRem(0.65, 0.9) }}>
      <PaymentElement
        options={{
          layout: 'tabs',
          wallets: {
            applePay: 'never',
            googlePay: 'never',
          },
        }}
      />
      {paymentError ? <div style={{ color: '#B00020', fontSize: scaledRem(0.68, 0.75) }}>{paymentError}</div> : null}
      <button
        type="button"
        onClick={() => { void handleSubmit(); }}
        disabled={!stripe || !elements || isSubmitting || isSaving}
        style={{
          backgroundColor: accentColor,
          color: '#FFFFFF',
          border: 'none',
          padding: `${scaledRem(0.62, 0.75)} ${scaledRem(0.9, 1.35)}`,
          borderRadius: scaledRem(0.5, 0.6),
          fontSize: scaledRem(0.72, 0.8),
          fontWeight: 700,
          cursor: !stripe || !elements || isSubmitting || isSaving ? 'wait' : 'pointer',
          opacity: !stripe || !elements || isSubmitting || isSaving ? 0.7 : 1,
        }}
      >
        {isSubmitting || isSaving ? 'Processing deposit…' : 'Pay deposit and confirm'}
      </button>
    </div>
  );
};

const resolveFontTheme = (fontThemeName?: string | null) => {
  if (!fontThemeName) {
    return FONT_THEME.CLASSIC;
  }

  return FONT_THEME[fontThemeName as keyof typeof FONT_THEME] ?? FONT_THEME.CLASSIC;
};

const toDateKey = (date: Date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const toMonthKey = (date: Date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  return `${year}-${month}`;
};

const getAppointmentLocalSelection = (isoTime: string, timezone?: string) => {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });
  const parts = Object.fromEntries(
    formatter.formatToParts(new Date(isoTime)).map((part) => [part.type, part.value]),
  );
  return {
    date: new Date(`${parts.year}-${parts.month}-${parts.day}T12:00:00`),
    time: `${parts.hour}:${parts.minute}`,
  };
};

const getDateInTimeZone = (timezone?: string) => {
  try {
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    const parts = formatter.formatToParts(new Date());
    const year = Number(parts.find((part) => part.type === 'year')?.value);
    const month = Number(parts.find((part) => part.type === 'month')?.value);
    const day = Number(parts.find((part) => part.type === 'day')?.value);
    return new Date(year, month - 1, day);
  } catch {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), today.getDate());
  }
};

const resolveMaxBookableDate = (
  bookingSettings?: PublicBookingPageResponse['business']['bookingSettings'],
  timezone?: string,
) => {
  const today = getDateInTimeZone(timezone);
  const maxBookableDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const advanceValue = bookingSettings?.advanceBookingValue ?? 30;
  const advanceUnit = bookingSettings?.advanceBookingUnit ?? 'DAYS';

  if (advanceUnit === 'WEEKS') {
    maxBookableDate.setDate(maxBookableDate.getDate() + (advanceValue * 7));
  } else if (advanceUnit === 'MONTHS') {
    maxBookableDate.setMonth(maxBookableDate.getMonth() + advanceValue);
  } else {
    maxBookableDate.setDate(maxBookableDate.getDate() + advanceValue);
  }

  return maxBookableDate;
};

const formatDisplayTime = (time: string) => {
  const [hourText, minute] = time.split(':');
  const hour = Number(hourText);
  const suffix = hour >= 12 ? 'PM' : 'AM';
  const normalizedHour = hour % 12 === 0 ? 12 : hour % 12;
  return `${normalizedHour}:${minute} ${suffix}`;
};

const formatAppointmentDateTime = (isoTime: string, timezone?: string) => {
  try {
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: timezone,
    }).format(new Date(isoTime));
  } catch {
    return new Date(isoTime).toLocaleString();
  }
};

const groupTimesByPeriod = (times: string[]): Record<PeriodKey, string[]> => {
  return times.reduce<Record<PeriodKey, string[]>>(
    (groups, time) => {
      const [hourText] = time.split(':');
      const hour = Number(hourText);
      if (hour < 12) {
        groups.Morning.push(formatDisplayTime(time));
      } else if (hour < 17) {
        groups.Afternoon.push(formatDisplayTime(time));
      } else {
        groups.Evening.push(formatDisplayTime(time));
      }
      return groups;
    },
    { Morning: [], Afternoon: [], Evening: [] },
  );
};

const STEP_LABELS: Array<{ key: Step; label: string; number: string }> = [
  { key: 'services', label: 'Service', number: '1' },
  { key: 'time', label: 'Time', number: '2' },
  { key: 'overview', label: 'Details', number: '3' },
  { key: 'confirmed', label: 'Confirmed', number: '4' },
];

const formatCurrency = (currency: string | undefined, amount: number) => `${currency || 'USD'} $${amount.toFixed(2)}`;
const trimText = (value?: string | null) => value?.trim() || '';

const toGoogleCalendarDateTime = (date: Date) => date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}Z$/, 'Z');

const BookingPage: React.FC<BookingPageProps> = ({
  bookingSlug: bookingSlugProp,
  previewData,
  previewAvailabilityByServiceId,
  previewMode = false,
  requireBookingSubdomainEntitlement = false,
}) => {
  const routeParams = useParams<{ bookingSlug: string }>();
  const location = useLocation();
  const bookingSlug = bookingSlugProp ?? routeParams.bookingSlug ?? '';
  const manageToken = useMemo(
    () => readUrlStateParam(location, 'manage'),
    [location],
  );
  const manageAction = useMemo(
    () => readUrlStateParam(location, 'action'),
    [location],
  );
  const baseScreen = useScreenSize();
  const isTouchLikeDevice = typeof window !== 'undefined'
    && (
      window.navigator.maxTouchPoints > 0
      || window.matchMedia('(pointer: coarse)').matches
      || window.matchMedia('(hover: none)').matches
    );
  const previewContainerRef = useRef<HTMLDivElement | null>(null);
  const [previewContainerWidth, setPreviewContainerWidth] = useState(0);
  const [bookingData, setBookingData] = useState<PublicBookingPageResponse | null>(previewData ?? null);
  const [isLoadingPage, setIsLoadingPage] = useState(!previewData);
  const [pageError, setPageError] = useState('');
  const [availabilityError, setAvailabilityError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFinalizingDeposit, setIsFinalizingDeposit] = useState(false);
  const [isAbandoningDeposit, setIsAbandoningDeposit] = useState(false);
  const [step, setStep] = useState<Step>('services');
  const [selectedServiceId, setSelectedServiceId] = useState<string | null>(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodKey | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [showMobileService, setShowMobileService] = useState(false);
  const [showMobileCalendar, setShowMobileCalendar] = useState(true);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [clientNotes, setClientNotes] = useState('');
  const [emailTouched, setEmailTouched] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [smsConsent, setSmsConsent] = useState(false);
  const [availabilityByServiceId, setAvailabilityByServiceId] = useState<Record<string, Record<string, string[]>>>({});
  const [loadedAvailabilityMonthsByServiceId, setLoadedAvailabilityMonthsByServiceId] = useState<Record<string, Record<string, boolean>>>({});
  const [loadingServiceIds, setLoadingServiceIds] = useState<Record<string, boolean>>({});
  const [manageDetails, setManageDetails] = useState<PublicManageAppointmentResponse | null>(null);
  const [manageError, setManageError] = useState('');
  const [isLoadingManage, setIsLoadingManage] = useState(false);
  const [showManageModal, setShowManageModal] = useState(false);
  const [isManageActionSubmitting, setIsManageActionSubmitting] = useState(false);
  const [isRescheduleMode, setIsRescheduleMode] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState('');
  const [pendingDepositResponse, setPendingDepositResponse] = useState<CreatePublicAppointmentResponse | null>(null);
  const availabilitySessionRef = useRef(0);
  const loadedAvailabilityMonthsRef = useRef<Record<string, Record<string, boolean>>>({});
  const inflightAvailabilityMonthKeysRef = useRef<Record<string, boolean>>({});
  const loadingServiceCountsRef = useRef<Record<string, number>>({});
  const smsNotificationsEnabled = businessHasFeature(bookingData?.business, PlanFeature.SMS_NOTIFICATIONS);
  const depositStripePromise = useMemo(
    () => (
      pendingDepositResponse?.publishableKey
      && pendingDepositResponse.paymentDetails?.stripeConnectedAccountId
        ? loadStripe(pendingDepositResponse.publishableKey, {
          stripeAccount: pendingDepositResponse.paymentDetails.stripeConnectedAccountId,
        })
        : null
    ),
    [
      pendingDepositResponse?.publishableKey,
      pendingDepositResponse?.paymentDetails?.stripeConnectedAccountId,
    ],
  );
  const depositElementsOptions = useMemo<StripeElementsOptions | null>(
    () => (pendingDepositResponse?.paymentDetails?.depositClientSecret
      ? {
        clientSecret: pendingDepositResponse.paymentDetails.depositClientSecret,
        appearance: { theme: 'stripe' },
      }
      : null),
    [pendingDepositResponse?.paymentDetails?.depositClientSecret],
  );
  const clearPendingDeposit = useCallback(() => {
    setPendingDepositResponse(null);
    setIsFinalizingDeposit(false);
  }, []);
  const abandonPendingDeposit = useCallback(async () => {
    const appointmentId = pendingDepositResponse?.appointment.id;
    const depositClientSecret = pendingDepositResponse?.paymentDetails?.depositClientSecret;
    if (!bookingData || !appointmentId || !depositClientSecret) {
      clearPendingDeposit();
      setStep('overview');
      return;
    }
    setIsAbandoningDeposit(true);
    setSubmitError('');
    try {
      const result = await abandonPublicDeposit(bookingData.bookingSlug, {
        appointmentId,
        depositClientSecret,
        abandonDeposit: true,
      });
      clearPendingDeposit();
      if (result.appointmentFinalized) {
        setConfirmationMessage('Your deposit was completed and your appointment is confirmed.');
        setStep('confirmed');
      } else {
        setStep('overview');
      }
    } catch (error) {
      setSubmitError(error instanceof Error
        ? error.message
        : 'Unable to release this appointment hold. Please try again.');
    } finally {
      setIsAbandoningDeposit(false);
    }
  }, [bookingData, clearPendingDeposit, pendingDepositResponse]);
  const applyFreshBookingContext = useCallback((
    response: Pick<CreatePublicAppointmentResponse, 'business' | 'provider' | 'service'>,
  ) => {
    setSelectedServiceId(response.service.id);
    setBookingData((current) => {
      if (!current) {
        return current;
      }
      const hasService = current.services.some((service) => service.id === response.service.id);
      return {
        ...current,
        business: response.business,
        provider: response.provider,
        services: hasService
          ? current.services.map((service) => (service.id === response.service.id ? response.service : service))
          : [...current.services, response.service],
      };
    });
  }, []);

  useEffect(() => {
    if (!previewMode) {
      setPreviewContainerWidth(0);
      return undefined;
    }

    const container = previewContainerRef.current;
    if (!container) {
      return undefined;
    }

    const updateWidth = () => {
      setPreviewContainerWidth(container.clientWidth);
    };

    updateWidth();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateWidth);
      return () => window.removeEventListener('resize', updateWidth);
    }

    const observer = new ResizeObserver(() => updateWidth());
    observer.observe(container);
    return () => observer.disconnect();
  }, [previewMode]);

  const responsiveWidth = previewMode && previewContainerWidth > 0 ? previewContainerWidth : baseScreen.width;
  const customerContactDetails = useMemo(() => getBusinessCustomerContactDetails(bookingData?.business), [bookingData?.business]);
  const appointmentInstructions = bookingData?.business?.bookingSettings?.appointmentInstructions?.trim() || null;
  const businessAddress = bookingData?.business?.contact?.address?.trim() || null;
  const screen = useMemo(() => ({
    width: responsiveWidth,
    isMobile: responsiveWidth < 768,
    isTablet: responsiveWidth >= 768 && responsiveWidth < 1024,
    isDesktop: responsiveWidth >= 1024,
  }), [responsiveWidth]);
  const useTouchStyleBookingFlow = isTouchLikeDevice && screen.width < 1280;
  const useTabletLikeInsetLayout = !screen.isMobile && screen.width < 1180;
  const useTabletThreeColumnTimeLayout = previewMode && !screen.isMobile && screen.width >= 760 && screen.width < 1180;
  const useCompactTimeStepLayout = previewMode && !screen.isMobile && screen.width < 760;
  const useCombinedCalendarTimeColumn = !previewMode && useTabletLikeInsetLayout;
  const useStickyMobileSummary = screen.width >= 480;
  const bookingIntroTitleSize = `${Math.min(1.4, Math.max(1.05, 0.9 + screen.width / 1600))}rem`;
  const bookingIntroCopySize = `${Math.min(1, Math.max(0.78, 0.68 + screen.width / 2500))}rem`;
  const bookingLayoutScale = Math.min(1, Math.max(0, (screen.width - 280) / 800));
  const scaledBookingRem = (minimum: number, maximum: number) =>
    `${minimum + (maximum - minimum) * bookingLayoutScale}rem`;
  const calendarGridGap = scaledBookingRem(0.16, 0.42);
  const calendarCellMinHeight = scaledBookingRem(2.5, 3.35);
  const calendarCellVerticalPadding = scaledBookingRem(0.28, 0.55);
  const calendarCellHorizontalPadding = scaledBookingRem(0.08, 0.3);
  const calendarCellRadius = scaledBookingRem(0.55, 0.8);
  const calendarDateSize = scaledBookingRem(0.68, 0.82);
  const calendarAvailabilityDotSize = scaledBookingRem(0.3, 0.38);

  useEffect(() => {
    if (previewData) {
      setBookingData(previewData);
      setIsLoadingPage(false);
      setPageError('');
      return undefined;
    }

    let isMounted = true;

    const loadBookingPage = async () => {
      if (!bookingSlug) {
        setPageError('Booking page could not be found.');
        setIsLoadingPage(false);
        return;
      }

      try {
        const response = await getPublicBookingPage(bookingSlug);
        if (!isMounted) {
          return;
        }
        setBookingData(response);
      } catch (error) {
        if (!isMounted) {
          return;
        }
        setPageError(error instanceof Error ? error.message : 'Unable to load booking page.');
      } finally {
        if (isMounted) {
          setIsLoadingPage(false);
        }
      }
    };

    void loadBookingPage();

    return () => {
      isMounted = false;
    };
  }, [bookingSlug, previewData]);

  useEffect(() => {
    if (previewMode || !bookingSlug || !manageToken) {
      setManageDetails(null);
      setManageError('');
      setShowManageModal(false);
      setIsRescheduleMode(false);
      return;
    }

    let isMounted = true;
    setIsLoadingManage(true);
    setManageError('');

    const loadManageDetails = async () => {
      try {
        const response = await getManagedPublicAppointment(bookingSlug, manageToken);
        if (!isMounted) {
          return;
        }
        setManageDetails(response);
        setFirstName(response.client.firstName || '');
        setLastName(response.client.lastName || '');
        setEmail(response.client.email || '');
        setPhone(response.client.phone || '');
        setMarketingConsent(response.client.allowEmail);
        setSmsConsent(response.client.allowSms);
        setSelectedServiceId(response.service.id);
        setShowManageModal(true);
      } catch (error) {
        if (!isMounted) {
          return;
        }
        setManageError(error instanceof Error ? error.message : 'Unable to load this appointment link.');
        setShowManageModal(true);
      } finally {
        if (isMounted) {
          setIsLoadingManage(false);
        }
      }
    };

    void loadManageDetails();

    return () => {
      isMounted = false;
    };
  }, [bookingSlug, manageToken, previewMode]);

  useEffect(() => {
    if (previewMode || manageAction !== 'reschedule' || !manageDetails || isRescheduleMode) {
      return;
    }

    setShowManageModal(false);
    setManageError('');
    setIsRescheduleMode(true);
    setSelectedServiceId(manageDetails.service.id);
    setCurrentMonth(new Date(manageDetails.appointment.start));
    setSelectedDate(null);
    resetTimeChoices();
    clearPendingDeposit();
    setSubmitError('');
    setConfirmationMessage('');
    setStep('time');
  }, [clearPendingDeposit, isRescheduleMode, manageAction, manageDetails, previewMode]);

  useEffect(() => {
    availabilitySessionRef.current += 1;
    loadedAvailabilityMonthsRef.current = {};
    inflightAvailabilityMonthKeysRef.current = {};
    loadingServiceCountsRef.current = {};
    setLoadingServiceIds({});
    setAvailabilityError('');

    if (previewMode && previewAvailabilityByServiceId) {
      setAvailabilityByServiceId(previewAvailabilityByServiceId);
      setLoadedAvailabilityMonthsByServiceId({});
      return;
    }

    setAvailabilityByServiceId({});
    setLoadedAvailabilityMonthsByServiceId({});
  }, [bookingSlug, previewAvailabilityByServiceId, previewMode]);

  const loadAvailabilityMonths = useCallback(async (
    serviceIds: string[],
    monthDates: Date[],
    surfaceErrors = false,
  ) => {
    if (!bookingData || previewMode || serviceIds.length === 0) {
      return;
    }

    const requestSession = availabilitySessionRef.current;
    const uniqueServiceIds = Array.from(new Set(serviceIds));
    const requestGroups = Array.from(new Set(monthDates.map((date) => toMonthKey(date))))
      .map((monthKey) => ({
        monthKey,
        serviceIds: uniqueServiceIds.filter((serviceId) => {
          const requestKey = `${serviceId}:${monthKey}`;
          return !loadedAvailabilityMonthsRef.current[serviceId]?.[monthKey]
            && !inflightAvailabilityMonthKeysRef.current[requestKey];
        }),
      }))
      .filter((group) => group.serviceIds.length > 0);

    if (requestGroups.length === 0) {
      return;
    }

    requestGroups.forEach(({ monthKey, serviceIds: groupServiceIds }) => {
      groupServiceIds.forEach((serviceId) => {
        inflightAvailabilityMonthKeysRef.current[`${serviceId}:${monthKey}`] = true;
        loadingServiceCountsRef.current[serviceId] = (loadingServiceCountsRef.current[serviceId] ?? 0) + 1;
      });
    });
    setLoadingServiceIds((current) => ({
      ...current,
      ...Object.fromEntries(uniqueServiceIds.map((serviceId) => [serviceId, true])),
    }));

    try {
      const results = await Promise.allSettled(
        requestGroups.map(async ({ monthKey, serviceIds: groupServiceIds }) => ({
          monthKey,
          serviceIds: groupServiceIds,
          response: await getPublicBookingAvailabilityBatch(
            bookingData.bookingSlug,
            groupServiceIds,
            monthKey,
            bookingData.provider.id,
          ),
        })),
      );

      if (requestSession !== availabilitySessionRef.current) {
        return;
      }

      const successfulResults = results.flatMap((result) => (
        result.status === 'fulfilled' ? [result.value] : []
      ));
      setAvailabilityByServiceId((current) => {
        const next = { ...current };
        successfulResults.forEach(({ response, serviceIds: groupServiceIds }) => {
          groupServiceIds.forEach((serviceId) => {
            const serviceAvailability = response.availableTimesByServiceId?.[serviceId]
              ?? (response.serviceId === serviceId ? response.availableTimesByDate : {});
            next[serviceId] = {
              ...(next[serviceId] ?? {}),
              ...serviceAvailability,
            };
          });
        });
        return next;
      });

      successfulResults.forEach(({ monthKey, serviceIds: groupServiceIds }) => {
        groupServiceIds.forEach((serviceId) => {
          if (!loadedAvailabilityMonthsRef.current[serviceId]) {
            loadedAvailabilityMonthsRef.current[serviceId] = {};
          }
          loadedAvailabilityMonthsRef.current[serviceId][monthKey] = true;
        });
      });
      setLoadedAvailabilityMonthsByServiceId((current) => ({
        ...successfulResults.reduce((next, { monthKey, serviceIds: groupServiceIds }) => {
          groupServiceIds.forEach((serviceId) => {
            next[serviceId] = {
              ...(next[serviceId] ?? current[serviceId] ?? {}),
              [monthKey]: true,
            };
          });
          return next;
        }, {} as Record<string, Record<string, boolean>>),
      }));
      if (surfaceErrors && successfulResults.length === 0) {
        const failedResult = results.find((result) => result.status === 'rejected');
        const error = failedResult?.status === 'rejected' ? failedResult.reason : null;
        setAvailabilityError(error instanceof Error ? error.message : 'Unable to load availability.');
      }
    } finally {
      if (requestSession === availabilitySessionRef.current) {
        requestGroups.forEach(({ monthKey, serviceIds: groupServiceIds }) => {
          groupServiceIds.forEach((serviceId) => {
            delete inflightAvailabilityMonthKeysRef.current[`${serviceId}:${monthKey}`];
            loadingServiceCountsRef.current[serviceId] = Math.max(
              (loadingServiceCountsRef.current[serviceId] ?? 1) - 1,
              0,
            );
          });
        });
        setLoadingServiceIds((current) => ({
          ...current,
          ...Object.fromEntries(uniqueServiceIds.map((serviceId) => [
            serviceId,
            (loadingServiceCountsRef.current[serviceId] ?? 0) > 0,
          ])),
        }));
      }
    }
  }, [bookingData, previewMode]);

  // Recover from a Stripe 3DS redirect: detect payment_intent params, restore saved state, and finalize
  useEffect(() => {
    if (!bookingData || previewMode) return;
    const params = new URLSearchParams(location.search);
    const paymentIntentId = params.get('payment_intent');
    const redirectStatus = params.get('redirect_status');
    if (!paymentIntentId) return;

    const savedKey = `lovibook_deposit_${bookingData.bookingSlug}`;
    const saved = sessionStorage.getItem(savedKey);

    if (redirectStatus !== 'succeeded') {
      setSubmitError(redirectStatus === 'failed' ? 'Payment failed. Please try again.' : 'Payment was not completed.');
      setStep('deposit');
      return;
    }

    let state: DepositReturnState | null = null;
    if (saved) {
      try {
        state = JSON.parse(saved) as DepositReturnState;
      } catch {
        state = null;
      }
    }

    setStep('deposit');
    setIsFinalizingDeposit(true);

    finalizePublicDeposit(bookingData.bookingSlug, {
      paymentIntentId,
      appointmentId: state?.appointmentId ?? '',
    }).then((response) => {
      applyFreshBookingContext(response);
      const restoredSelection = state
        ? {
          date: new Date(`${state.date}T12:00:00`),
          time: state.time,
        }
        : getAppointmentLocalSelection(response.appointment.start, response.business.timezone);
      setSelectedServiceId(response.service.id);
      setSelectedDate(restoredSelection.date);
      setSelectedTime(restoredSelection.time);
      setFirstName(response.client.firstName ?? '');
      setLastName(response.client.lastName ?? '');
      setEmail(response.client.email ?? '');
      setPhone(response.client.phone ?? '');
      sessionStorage.removeItem(savedKey);
      clearPendingDeposit();
      setStep('confirmed');
    }).catch((error: unknown) => {
      setSubmitError(error instanceof Error ? error.message : 'Unable to finalize the deposit payment.');
    }).finally(() => {
      setIsFinalizingDeposit(false);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingData?.bookingSlug]);

  const business = bookingData?.business;
  const provider = bookingData?.provider;
  const hasBookingSubdomain = businessHasFeature(business, PlanFeature.BOOKING_SUBDOMAIN);
  const isInactiveBookingSubdomain = requireBookingSubdomainEntitlement && !hasBookingSubdomain;
  const services = useMemo(() => bookingData?.services ?? [], [bookingData]);
  const selectedService = useMemo(
    () => services.find((service) => service.id === selectedServiceId) ?? null,
    [selectedServiceId, services],
  );
  const currentStepIndex = STEP_LABELS.findIndex((stepItem) => stepItem.key === (step === 'deposit' ? 'overview' : step));
  const publicPaymentSummary = bookingData?.paymentSummary ?? null;
  const depositReadyForBooking = Boolean(publicPaymentSummary?.depositRequired && publicPaymentSummary.paymentReady && !manageToken);
  const estimatedDepositDueToday = useMemo(() => {
    if (!selectedService || !publicPaymentSummary?.depositRequired) {
      return 0;
    }
    if (publicPaymentSummary.depositType === DepositType.PERCENTAGE) {
      return Math.min(selectedService.price, Number(((selectedService.price * publicPaymentSummary.depositValue) / 100).toFixed(2)));
    }
    return Math.min(selectedService.price, Number(publicPaymentSummary.depositValue.toFixed(2)));
  }, [publicPaymentSummary?.depositRequired, publicPaymentSummary?.depositType, publicPaymentSummary?.depositValue, selectedService]);

  const activeBranding = previewMode && previewData ? previewData.branding : bookingData?.branding;
  const activeColorScheme = resolveColorScheme(activeBranding?.colorSchemeName);
  const activeFontTheme = resolveFontTheme(activeBranding?.fontThemeName);
  useEffect(() => {
    const fontThemeName = activeBranding?.fontThemeName as keyof typeof FONT_THEME | undefined;
    if (fontThemeName && FONT_THEME[fontThemeName]) {
      preloadFontScheme(fontThemeName);
    }
  }, [activeBranding?.fontThemeName]);
  const manageAppointmentLabel = useMemo(
    () => (
      manageDetails
        ? formatAppointmentDateTime(manageDetails.appointment.start, manageDetails.business.timezone)
        : ''
    ),
    [manageDetails],
  );
  const headerVariation = Object.values(HeaderCardVariationTypes).includes(
    activeBranding?.headerStyle as HeaderCardVariationTypes,
  )
    ? (activeBranding?.headerStyle as HeaderCardVariationTypes)
    : HeaderCardVariationTypes.CLASSIC;
  const pageHorizontalPadding = !previewMode && screen.isDesktop
    ? '0'
    : scaledBookingRem(0.65, previewMode ? 1.5 : 1.2);
  const pagePadding = `${scaledBookingRem(previewMode ? 1.2 : 1.5, previewMode ? 2.6 : 3)} ${pageHorizontalPadding} ${scaledBookingRem(previewMode ? 1.45 : 1.7, previewMode ? 2.8 : 3)}`;

  const availableTimesByDate = useMemo(
    () => (selectedServiceId ? availabilityByServiceId[selectedServiceId] ?? {} : {}),
    [availabilityByServiceId, selectedServiceId],
  );
  const currentMonthKey = useMemo(() => toMonthKey(currentMonth), [currentMonth]);
  const isCurrentMonthAvailabilityLoaded = useMemo(
    () => (selectedServiceId ? Boolean(loadedAvailabilityMonthsByServiceId[selectedServiceId]?.[currentMonthKey]) : false),
    [currentMonthKey, loadedAvailabilityMonthsByServiceId, selectedServiceId],
  );
  const selectedDateKey = selectedDate ? toDateKey(selectedDate) : null;
  const selectedDateTimes = useMemo(
    () => (selectedDateKey ? availableTimesByDate[selectedDateKey] ?? [] : []),
    [availableTimesByDate, selectedDateKey],
  );
  const earliestBookableDate = useMemo(
    () => getDateInTimeZone(business?.timezone),
    [business?.timezone],
  );
  const maxBookableDate = useMemo(
    () => resolveMaxBookableDate(business?.bookingSettings, business?.timezone),
    [business?.bookingSettings, business?.timezone],
  );
  const periodTimes = useMemo(() => groupTimesByPeriod(selectedDateTimes), [selectedDateTimes]);
  const nextAvailableDate = useMemo(() => {
    if (!selectedDate) {
      return null;
    }

    const sortedAvailableDates = Object.keys(availableTimesByDate)
      .filter((dateKey) => Array.isArray(availableTimesByDate[dateKey]) && availableTimesByDate[dateKey].length > 0)
      .sort();
    const selectedDateKeyValue = toDateKey(selectedDate);
    const nextDateKey = sortedAvailableDates.find((dateKey) => dateKey > selectedDateKeyValue);
    if (!nextDateKey) {
      return null;
    }

    const [year, month, day] = nextDateKey.split('-').map(Number);
    return new Date(year, month - 1, day);
  }, [availableTimesByDate, selectedDate]);
  const monthLabel = currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' });
  const startOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
  const endOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
  const earliestBookableMonth = new Date(earliestBookableDate.getFullYear(), earliestBookableDate.getMonth(), 1);
  const maxBookableMonth = new Date(maxBookableDate.getFullYear(), maxBookableDate.getMonth(), 1);
  const earliestBookableMonthTime = earliestBookableMonth.getTime();
  const maxBookableMonthTime = maxBookableMonth.getTime();
  const canGoToPreviousMonth = startOfMonth.getTime() > earliestBookableMonth.getTime();
  const canGoToNextMonth = startOfMonth.getTime() < maxBookableMonth.getTime();
  const days = Array.from({ length: endOfMonth.getDate() }, (_, index) => (
    new Date(currentMonth.getFullYear(), currentMonth.getMonth(), index + 1)
  ));
  const bookingSubdomainSlug = getBookingSubdomainSlug(window.location.hostname);
  const isActiveBookingSubdomain = hasBookingSubdomain && bookingSubdomainSlug === bookingSlug;
  const isIndexablePublicBookingPage = !previewMode
    && (isPublicProductionHost(window.location.hostname) || isActiveBookingSubdomain)
    && !manageToken
    && Boolean(bookingSlug);
  const bookingCanonicalUrl = isIndexablePublicBookingPage
    ? buildBookingPageUrl(bookingSlug, hasBookingSubdomain)
    : null;
  const bookingPageTitle = useMemo(() => {
    if (!bookingData?.business?.name) {
      return 'Book Appointment';
    }
    return `Book with ${bookingData.business.name}`;
  }, [bookingData?.business?.name]);
  const bookingPageDescription = useMemo(() => {
    const businessName = trimText(bookingData?.business?.name);
    const serviceNames = services
      .map((service) => trimText(service.name))
      .filter(Boolean)
      .slice(0, 3);
    const addressText = trimText(bookingData?.business?.contact?.address);

    const descriptionParts = [
      businessName ? `Book an appointment online with ${businessName}.` : 'Book an appointment online with LoviBook.',
      serviceNames.length > 0 ? `Services include ${serviceNames.join(', ')}${services.length > 3 ? ', and more' : ''}.` : '',
      addressText ? `Location: ${addressText}.` : '',
    ].filter(Boolean);

    return descriptionParts.join(' ');
  }, [bookingData?.business?.contact?.address, bookingData?.business?.name, services]);
  const bookingPageSchema = useMemo(() => {
    if (!isIndexablePublicBookingPage || !bookingData?.business) {
      return null;
    }

    const businessName = trimText(bookingData.business.name);
    const addressText = trimText(bookingData.business.contact?.address);
    const contactEmail = trimText(bookingData.business.contact?.email);
    const contactPhone = trimText(bookingData.business.contact?.phone);
    const imageUrl = trimText(bookingData.business.logoUrl) || `${PUBLIC_SITE_ORIGIN}/logo.png?v=20260422c`;

    return {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      name: businessName || 'LoviBook business',
      url: buildBookingPageUrl(bookingSlug, hasBookingSubdomain),
      image: imageUrl,
      description: bookingPageDescription,
      address: addressText
        ? {
          '@type': 'PostalAddress',
          streetAddress: addressText,
        }
        : undefined,
      email: contactEmail || undefined,
      telephone: contactPhone || undefined,
      makesOffer: services.slice(0, 10).map((service) => ({
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: trimText(service.name),
          description: trimText(service.description) || undefined,
        },
        price: service.price.toFixed(2),
        priceCurrency: service.currency || 'USD',
      })),
    };
  }, [bookingData?.business, bookingPageDescription, bookingSlug, hasBookingSubdomain, isIndexablePublicBookingPage, services]);

  useEffect(() => {
    setCurrentMonth((previousMonth) => {
      const normalizedMonth = new Date(previousMonth.getFullYear(), previousMonth.getMonth(), 1);
      const normalizedMonthTime = normalizedMonth.getTime();
      if (normalizedMonthTime < earliestBookableMonthTime) {
        return new Date(earliestBookableMonthTime);
      }
      if (normalizedMonthTime > maxBookableMonthTime) {
        return new Date(maxBookableMonthTime);
      }
      if (previousMonth.getDate() !== 1) {
        return normalizedMonth;
      }
      return previousMonth;
    });
  }, [earliestBookableMonthTime, maxBookableMonthTime]);

  useEffect(() => {
    if (selectedDate && !availableTimesByDate[toDateKey(selectedDate)]) {
      setSelectedDate(null);
      setSelectedPeriod(null);
      setSelectedTime(null);
    }
  }, [availableTimesByDate, selectedDate]);

  useEffect(() => {
    if (!bookingData || previewMode || selectedServiceId) {
      return;
    }

    const visibleMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    void loadAvailabilityMonths(
      bookingData.services.map((service) => service.id),
      [visibleMonth],
    );
  }, [bookingData, currentMonth, loadAvailabilityMonths, previewMode, selectedServiceId]);

  useEffect(() => {
    if (!selectedServiceId || previewMode) {
      return;
    }

    const monthsToLoad = [new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1)];
    const nextMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);
    if (nextMonth.getTime() <= maxBookableMonthTime) {
      monthsToLoad.push(nextMonth);
    }

    void loadAvailabilityMonths([selectedServiceId], monthsToLoad, true);
  }, [currentMonth, loadAvailabilityMonths, maxBookableMonthTime, previewMode, selectedServiceId]);

  const resetTimeChoices = () => {
    setSelectedPeriod(null);
    setSelectedTime(null);
  };

  const isManageFlow = Boolean(manageToken);
  const isServiceLocked = isManageFlow && isRescheduleMode && Boolean(manageDetails);
  const selectedTimeLabel = selectedTime ? formatDisplayTime(selectedTime) : '';
  const overviewPanelMaxWidth = screen.isMobile ? '100%' : '620px';
  const googleCalendarUrl = useMemo(() => {
    if (!selectedService || !selectedDate || !selectedTime) {
      return '';
    }
    const [hoursText, minutesText] = selectedTime.split(':');
    const startAt = new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth(),
      selectedDate.getDate(),
      Number(hoursText),
      Number(minutesText),
      0,
      0,
    );
    const endAt = new Date(startAt.getTime() + (selectedService.durationMinutes * 60 * 1000));
    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: `${selectedService.name} with ${business?.name || provider?.name || 'LoviBook'}`,
      dates: `${toGoogleCalendarDateTime(startAt)}/${toGoogleCalendarDateTime(endAt)}`,
      details: `Booked with ${business?.name || provider?.name || 'LoviBook'} via LoviBook.`,
    });
    if (business?.contact?.address) {
      params.set('location', business.contact.address);
    }
    return `https://calendar.google.com/calendar/render?${params.toString()}`;
  }, [business?.contact?.address, business?.name, provider?.name, selectedDate, selectedService, selectedTime]);

  const startRescheduleFlow = () => {
    if (!manageDetails) {
      return;
    }
    setShowManageModal(false);
    setManageError('');
    setIsRescheduleMode(true);
    setSelectedServiceId(manageDetails.service.id);
    setCurrentMonth(new Date(manageDetails.appointment.start));
    setSelectedDate(null);
    setShowMobileCalendar(true);
    resetTimeChoices();
    clearPendingDeposit();
    setSubmitError('');
    setConfirmationMessage('');
    setStep('time');
  };

  const handleCancelManagedAppointment = async () => {
    if (!manageToken || !bookingSlug) {
      return;
    }
    setIsManageActionSubmitting(true);
    setManageError('');
    try {
      const response = await cancelManagedPublicAppointment(bookingSlug, { token: manageToken });
      setManageDetails(response);
      setIsRescheduleMode(false);
      setConfirmationMessage('');
      setStep('services');
    } catch (error) {
      setManageError(error instanceof Error ? error.message : 'Unable to cancel appointment.');
    } finally {
      setIsManageActionSubmitting(false);
      setShowManageModal(true);
    }
  };

  const handleSelectService = (service: PublicBookingService) => {
    if (isServiceLocked) {
      return;
    }
    setSelectedServiceId(service.id);
    setStep('time');
    setSelectedDate(null);
    setShowMobileCalendar(true);
    resetTimeChoices();
    clearPendingDeposit();
    setAvailabilityError('');
    setSubmitError('');
  };

  const isLoadingAvailability = selectedServiceId ? Boolean(loadingServiceIds[selectedServiceId]) : false;
  const showMonthAvailabilityLoadingState = isLoadingAvailability && !isCurrentMonthAvailabilityLoaded;

  const handleSelectDay = (date: Date) => {
    const dateKey = toDateKey(date);
    const isWithinBookingWindow =
      date.getTime() >= earliestBookableDate.getTime() && date.getTime() <= maxBookableDate.getTime();
    if (!isWithinBookingWindow || !availableTimesByDate[dateKey]) {
      return;
    }
    setSelectedDate(date);
    if (screen.isMobile || useTouchStyleBookingFlow) {
      setShowMobileCalendar(false);
    }
    resetTimeChoices();
  };

  const handleChooseDifferentDay = () => {
    setSelectedDate(null);
    resetTimeChoices();
    setShowMobileCalendar(true);
  };

  const handleSelectNextAvailableDay = () => {
    if (!nextAvailableDate) {
      return;
    }

    setCurrentMonth(new Date(nextAvailableDate.getFullYear(), nextAvailableDate.getMonth(), 1));
    setSelectedDate(nextAvailableDate);
    resetTimeChoices();
    setShowMobileCalendar(false);
  };

  const proceedToOverview = (displayTime: string) => {
    const matchingTime = selectedDateTimes.find((time) => formatDisplayTime(time) === displayTime);
    if (!matchingTime) {
      return;
    }

    setSelectedTime(matchingTime);
    clearPendingDeposit();
    setSubmitError('');
    setStep('overview');
  };

  const formattedOverviewDate = selectedDate
    ? (screen.isMobile
      ? `${selectedDate.toLocaleString('default', { month: 'short' }).toUpperCase()} ${selectedDate
        .toLocaleString('default', { weekday: 'short' })
        .toUpperCase()} ${selectedDate.getDate()}`
      : selectedDate.toLocaleString('default', { weekday: 'long', month: 'short', day: 'numeric' }).toUpperCase())
    : '';

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!bookingData || !selectedService || !selectedDate || !selectedTime) {
      setSubmitError('Please choose a service, date, and time.');
      return;
    }

    if (!email.trim()) {
      setEmailTouched(true);
      setSubmitError('Email is required.');
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    if (previewMode) {
      setStep('confirmed');
      setIsSubmitting(false);
      return;
    }

    // When a deposit is required, show the deposit confirmation interstitial first
    // before making any API call. The actual booking happens from the deposit step.
    if (depositReadyForBooking && !isManageFlow) {
      setIsSubmitting(false);
      setSubmitError('');
      setStep('deposit');
      return;
    }

    try {
      if (isManageFlow && isRescheduleMode && manageToken) {
        const response = await rescheduleManagedPublicAppointment(bookingData.bookingSlug, {
          token: manageToken,
          serviceId: selectedService.id,
          date: toDateKey(selectedDate),
          time: selectedTime,
        });
        applyFreshBookingContext(response);
        setConfirmationMessage('Your appointment has been rescheduled. We sent a fresh manage link to your email.');
        setManageDetails(null);
        setIsRescheduleMode(false);
        setShowManageModal(false);
      } else {
        const response = await createPublicAppointment(bookingData.bookingSlug, {
          providerId: bookingData.provider.id,
          serviceId: selectedService.id,
          date: toDateKey(selectedDate),
          time: selectedTime,
          firstName,
          lastName,
          email,
          phone,
          marketingConsent,
          smsConsent: smsNotificationsEnabled ? smsConsent : false,
          clientNotes: clientNotes.trim() || undefined,
        });
        applyFreshBookingContext(response);
        if (response.requiresPayment) {
          setPendingDepositResponse(response);
          setConfirmationMessage('');
          setStep('deposit');
          return;
        }
        clearPendingDeposit();
        setConfirmationMessage('');
      }
      setStep('confirmed');
    } catch (error) {
      const msg = error instanceof Error ? error.message : '';
      if (msg.toLowerCase().includes('deposit') || msg.toLowerCase().includes('stripe')) {
        setSubmitError('Unable to collect the deposit payment. Please contact the business to complete your booking.');
      } else {
        setSubmitError(msg || (isRescheduleMode ? 'Unable to reschedule appointment.' : 'Unable to confirm appointment.'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const finalizeDepositAppointment = async (paymentIntentId: string) => {
    if (!bookingData || !selectedService || !selectedDate || !selectedTime) {
      throw new Error('Please confirm your service, date, and time again.');
    }

    setIsFinalizingDeposit(true);
    setSubmitError('');

    try {
      const response = await finalizePublicDeposit(bookingData.bookingSlug, {
        paymentIntentId,
        appointmentId: pendingDepositResponse?.appointment.id ?? '',
      });
      applyFreshBookingContext(response);
      clearPendingDeposit();
      setConfirmationMessage('');
      setStep('confirmed');
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Unable to finalize the deposit payment.');
      throw error;
    } finally {
      setIsFinalizingDeposit(false);
    }
  };

  const confirmDepositAndBook = async () => {
    if (!bookingData || !selectedService || !selectedDate || !selectedTime) return;
    setIsSubmitting(true);
    setSubmitError('');
    try {
      const response = await createPublicAppointment(bookingData.bookingSlug, {
        providerId: bookingData.provider.id,
        serviceId: selectedService.id,
        date: toDateKey(selectedDate),
        time: selectedTime,
        firstName,
        lastName,
        email,
        phone,
        marketingConsent,
        smsConsent: smsNotificationsEnabled ? smsConsent : false,
        clientNotes: clientNotes.trim() || undefined,
      });
      applyFreshBookingContext(response);
      if (response.requiresPayment) {
        setPendingDepositResponse(response);
        setConfirmationMessage('');
        setStep('deposit');
        // Save state so we can recover if Stripe redirects for 3DS auth
        try {
          const returnState: DepositReturnState = {
            appointmentId: response.appointment.id,
            serviceId: selectedService.id,
            date: toDateKey(selectedDate),
            time: selectedTime,
            firstName,
            lastName,
            email,
            phone,
            marketingConsent,
            smsConsent: smsNotificationsEnabled ? smsConsent : false,
            clientNotes: clientNotes.trim(),
          };
          sessionStorage.setItem(`lovibook_deposit_${bookingData.bookingSlug}`, JSON.stringify(returnState));
        } catch { /* non-critical */ }
        return;
      }
      clearPendingDeposit();
      setConfirmationMessage('');
      setStep('confirmed');
    } catch (error) {
      const msg = error instanceof Error ? error.message : '';
      if (msg.toLowerCase().includes('deposit') || msg.toLowerCase().includes('stripe')) {
        setSubmitError('Unable to collect the deposit payment. Please contact the business to complete your booking.');
      } else {
        setSubmitError(msg || 'Unable to confirm appointment.');
      }
      // Stay on the deposit step so the error shows on the interstitial, not the form
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetBookingFlow = () => {
    setStep('services');
    setSelectedServiceId(isManageFlow && manageDetails ? manageDetails.service.id : null);
    setSelectedDate(null);
    setShowMobileCalendar(true);
    setSelectedPeriod(null);
    setSelectedTime(null);
    setFirstName(isManageFlow && manageDetails ? manageDetails.client.firstName || '' : '');
    setLastName(isManageFlow && manageDetails ? manageDetails.client.lastName || '' : '');
    setEmail(isManageFlow && manageDetails ? manageDetails.client.email || '' : '');
    setPhone(isManageFlow && manageDetails ? manageDetails.client.phone || '' : '');
    setClientNotes('');
    setMarketingConsent(isManageFlow && manageDetails ? manageDetails.client.allowEmail : false);
    setSmsConsent(isManageFlow && manageDetails ? manageDetails.client.allowSms : false);
    setIsRescheduleMode(false);
    clearPendingDeposit();
    setSubmitError('');
    setConfirmationMessage('');
    setEmailTouched(false);
  };

  const renderDayCell = (day: Date) => {
    const dateKey = toDateKey(day);
    const isWithinBookingWindow =
      day.getTime() >= earliestBookableDate.getTime() && day.getTime() <= maxBookableDate.getTime();
    const isAvailable = isWithinBookingWindow && Boolean(availableTimesByDate[dateKey]);
    const isSelected = selectedDate?.toDateString() === day.toDateString();

    return (
      <button
        key={day.toDateString()}
        type="button"
        onClick={() => handleSelectDay(day)}
        disabled={!isAvailable}
        style={{
          padding: `${calendarCellVerticalPadding} ${calendarCellHorizontalPadding}`,
          minHeight: calendarCellMinHeight,
          borderRadius: calendarCellRadius,
          textAlign: 'center',
          cursor: isAvailable ? 'pointer' : 'not-allowed',
          border: isSelected ? `2px solid ${activeColorScheme.PRIMARY}` : `1px solid ${isAvailable ? `${activeColorScheme.PRIMARY}30` : '#E5E7EB'}`,
          background: isSelected ? activeColorScheme.PRIMARY : isAvailable ? '#FFFFFF' : '#F8FAFC',
          color: isSelected ? '#FFFFFF' : isAvailable ? '#1F2937' : '#9CA3AF',
          transition: 'all 0.2s ease',
          opacity: isAvailable ? 1 : 0.72,
          boxShadow: isSelected
            ? `0 0.7rem 1.4rem ${activeColorScheme.PRIMARY}30`
            : isAvailable
              ? '0 0.25rem 0.65rem rgba(15, 23, 42, 0.06)'
              : 'none',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.18rem',
        }}
      >
        <span style={{ fontSize: calendarDateSize, fontWeight: 800 }}>
          {day.getDate()}
        </span>
        <span
          style={{
            width: calendarAvailabilityDotSize,
            height: calendarAvailabilityDotSize,
            borderRadius: '999px',
            background: isSelected ? '#FFFFFF' : isAvailable ? activeColorScheme.PRIMARY : '#D1D5DB',
          }}
        />
      </button>
    );
  };

  const renderCalendarPanel = () => (
    <div style={{ padding: useTabletLikeInsetLayout ? '0.1rem 0.2rem 0.2rem' : undefined }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.65rem' }}>
        <button
          type="button"
          onClick={() => canGoToPreviousMonth && setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))}
          disabled={!canGoToPreviousMonth}
          style={{ background: 'transparent', border: 'none', cursor: canGoToPreviousMonth ? 'pointer' : 'not-allowed', fontSize: '1rem', opacity: canGoToPreviousMonth ? 1 : 0.35 }}
        >
          ‹
        </button>
        <div style={{ fontWeight: 800, color: '#111827' }}>{monthLabel}</div>
        <button
          type="button"
          onClick={() => canGoToNextMonth && setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))}
          disabled={!canGoToNextMonth}
          style={{ background: 'transparent', border: 'none', cursor: canGoToNextMonth ? 'pointer' : 'not-allowed', fontSize: '1rem', opacity: canGoToNextMonth ? 1 : 0.35 }}
        >
          ›
        </button>
      </div>
      <div style={{ display: 'flex', gap: '0.7rem', flexWrap: 'wrap', marginBottom: '0.75rem', fontSize: '0.67rem', color: '#6B7280' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}><span style={{ width: '0.45rem', height: '0.45rem', borderRadius: '999px', background: activeColorScheme.PRIMARY, display: 'inline-block' }} /> Available</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}><span style={{ width: '0.45rem', height: '0.45rem', borderRadius: '999px', background: '#D1D5DB', display: 'inline-block' }} /> Unavailable</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}><span style={{ width: '0.8rem', height: '0.8rem', borderRadius: '999px', border: `2px solid ${activeColorScheme.PRIMARY}`, display: 'inline-block' }} /> Selected</span>
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7,1fr)',
          gap: calendarGridGap,
          fontSize: '0.62rem',
          marginBottom: '0.35rem',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: '#6B7280',
        }}
      >
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((dayLabel) => (
          <div key={dayLabel} style={{ textAlign: 'center', padding: '0.2rem 0' }}>
            {dayLabel}
          </div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: calendarGridGap }}>
        {Array.from({ length: startOfMonth.getDay() }).map((_, index) => <div key={`blank-${index}`} />)}
        {days.map((day) => renderDayCell(day))}
      </div>
    </div>
  );

  if (isLoadingPage) {
    return <div style={{ width: '100%', padding: '4rem 1rem', textAlign: 'center' }}>Loading booking page…</div>;
  }

  if (pageError || !bookingData || !business || !provider || isInactiveBookingSubdomain) {
    const isUnavailable = pageError === 'This booking page is currently unavailable.';
    const isInactiveCustomUrl = isInactiveBookingSubdomain && Boolean(bookingData && business && provider);
    return (
      <>
        <PageSeo
          title={isInactiveCustomUrl ? 'Custom Booking Address Inactive' : 'Booking Page Unavailable'}
          description="This LoviBook booking page is not currently available."
          robots="noindex, nofollow"
          canonicalPath={null}
        />
        <div style={{
          width: '100%',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '4rem 1.5rem',
          background: '#F8FAF7',
          fontFamily: 'Poppins, sans-serif',
        }}>
          <div style={{
            maxWidth: 420,
            width: '100%',
            background: '#fff',
            borderRadius: '1.2rem',
            border: '1px solid #E5EAE3',
            boxShadow: '0 4px 24px rgba(15,23,42,0.07)',
            padding: '2.5rem 2rem',
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '0.75rem',
          }}>
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#F0F7EE', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '0.25rem' }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#4A7C59" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
              </svg>
            </div>
            <div style={{ fontWeight: 700, fontSize: '1.15rem', color: '#1F2B1C' }}>
              {isUnavailable
                ? 'Online booking is not available'
                : isInactiveCustomUrl
                  ? 'This custom booking address is inactive'
                  : 'Booking page not found'}
            </div>
            <div style={{ fontSize: '0.9rem', color: '#5C6B5A', lineHeight: 1.6 }}>
              {isUnavailable
                ? 'This business is not currently accepting online bookings. Please contact them directly to schedule an appointment.'
                : isInactiveCustomUrl
                  ? 'This business is not currently using its custom LoviBook address.'
                  : 'We could not find this booking page. The link may be incorrect or this business may no longer be active.'}
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <HeaderCard
        title={business.name}
        variation={headerVariation}
        colorScheme={activeColorScheme}
        fontScheme={activeFontTheme}
        logoSrc={bookingData.branding?.logoUrl ?? undefined}
        height="80px"
        isFixed={!previewMode}
        brandHref={previewMode ? null : '/'}
      />
      <div
        ref={previewContainerRef}
        style={{
          maxWidth: screen.isMobile ? '100%' : '1080px',
          padding: pagePadding,
          fontFamily: activeFontTheme?.BODY || 'Poppins, sans-serif',
          width: '100%',
          boxSizing: 'border-box',
          margin: '0 auto',
        }}
      >
        {isIndexablePublicBookingPage ? (
          <PageSeo
            title={bookingPageTitle}
            description={bookingPageDescription}
            robots="index, follow"
            canonicalPath={bookingCanonicalUrl}
            alternates={bookingCanonicalUrl ? [
              { hrefLang: 'en', href: bookingCanonicalUrl },
              { hrefLang: 'x-default', href: bookingCanonicalUrl },
            ] : []}
            schema={bookingPageSchema}
            imagePath={trimText(bookingData?.business?.logoUrl) || '/logo.png?v=20260422c'}
          />
        ) : null}
        <div
          style={{
            display: 'flex',
            gap: scaledBookingRem(0.2, 0.75),
            justifyContent: 'center',
            flexWrap: screen.isMobile ? 'nowrap' : 'wrap',
            marginBottom: scaledBookingRem(0.7, 1),
          }}
        >
          {STEP_LABELS.map((stepItem, index) => {
            const isComplete = currentStepIndex > index;
            const isActive = currentStepIndex === index;
            return (
              <div
                key={stepItem.key}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: scaledBookingRem(0.16, 0.45),
                  padding: `${scaledBookingRem(0.28, 0.55)} ${scaledBookingRem(0.32, 0.9)}`,
                  borderRadius: '999px',
                  border: `1px solid ${isActive || isComplete ? `${activeColorScheme.PRIMARY}55` : '#D8DCE8'}`,
                  background: isActive ? `${activeColorScheme.PRIMARY}12` : isComplete ? '#F3F7FF' : '#FFFFFF',
                  boxShadow: isActive ? `0 0.45rem 1rem ${activeColorScheme.PRIMARY}16` : 'none',
                  minWidth: 0,
                }}
              >
                <span
                  style={{
                    width: scaledBookingRem(0.9, 1.5),
                    height: scaledBookingRem(0.9, 1.5),
                    borderRadius: '999px',
                    background: isActive || isComplete ? activeColorScheme.PRIMARY : '#E5E7EB',
                    color: '#FFFFFF',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: scaledBookingRem(0.5, 0.72),
                    fontWeight: 800,
                    flexShrink: 0,
                  }}
                >
                  {isComplete ? stepItem.number : stepItem.number}
                </span>
                <span style={{ fontSize: scaledBookingRem(0.52, 0.76), fontWeight: 700, color: isActive || isComplete ? '#111827' : '#6B7280', whiteSpace: 'nowrap' }}>
                  {stepItem.label}
                </span>
              </div>
            );
          })}
        </div>

        {isManageFlow && isRescheduleMode && manageDetails && (
          <div
            style={{
              ...cardBase,
              maxWidth: '720px',
              margin: '0 auto 1.5rem',
              border: `1px solid ${activeColorScheme.PRIMARY}`,
              background: `${activeColorScheme.PRIMARY}10`,
            }}
          >
            <div style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', color: activeColorScheme.PRIMARY }}>
              Rescheduling Appointment
            </div>
            <div style={{ fontSize: '0.95rem', fontWeight: 700 }}>{manageDetails.service.name}</div>
            <div style={{ fontSize: '0.78rem', color: '#444' }}>
              Current appointment: {manageAppointmentLabel}
            </div>
            <div style={{ fontSize: '0.72rem', color: '#555' }}>
              Pick a new time below. Your old appointment will be cancelled as soon as the new one is confirmed.
            </div>
          </div>
        )}

        {showManageModal && !previewMode && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(15, 23, 42, 0.45)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: scaledBookingRem(0.65, 1),
              zIndex: 2000,
            }}
          >
            <div
              style={{
                ...cardBase,
                width: '100%',
                maxWidth: '520px',
                padding: `${scaledBookingRem(0.9, 1.5)} ${scaledBookingRem(0.8, 1.4)} ${scaledBookingRem(1, 1.55)}`,
                boxSizing: 'border-box',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
                <div>
                  <h2 style={{ margin: 0, fontSize: scaledBookingRem(1, 1.2), fontWeight: 700, color: activeColorScheme.PRIMARY }}>
                    Manage Appointment
                  </h2>
                  <p style={{ margin: `${scaledBookingRem(0.3, 0.45)} 0 0`, fontSize: scaledBookingRem(0.7, 0.78), color: '#555' }}>
                    Cancel this visit or choose a new appointment time.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowManageModal(false)}
                  style={{ background: 'transparent', border: 'none', fontSize: '1.1rem', cursor: 'pointer', color: '#666' }}
                  aria-label="Close appointment manager"
                >
                  ×
                </button>
              </div>

              {isLoadingManage && (
                <div style={{ fontSize: '0.8rem', color: '#555' }}>Loading your appointment details…</div>
              )}

              {!isLoadingManage && manageError && (
                <div style={{ color: '#B00020', fontSize: '0.8rem', lineHeight: 1.5 }}>{manageError}</div>
              )}

              {!isLoadingManage && !manageError && manageDetails && (
                <>
                  <div style={{ ...cardBase, boxShadow: 'none', padding: `${scaledBookingRem(0.7, 0.95)} ${scaledBookingRem(0.7, 1)}`, background: '#FAFAFA' }}>
                    <div style={{ fontSize: scaledBookingRem(0.62, 0.7), fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', color: activeColorScheme.PRIMARY }}>
                      Current Appointment
                    </div>
                    <div style={{ fontSize: scaledBookingRem(0.82, 0.95), fontWeight: 700 }}>{manageDetails.service.name}</div>
                    <div style={{ fontSize: scaledBookingRem(0.7, 0.8), color: '#444' }}>{manageAppointmentLabel}</div>
                    <div style={{ fontSize: scaledBookingRem(0.66, 0.75), color: '#666' }}>
                      with {manageDetails.provider.name || manageDetails.business.name}
                    </div>
                  </div>

                  <div style={{ fontSize: scaledBookingRem(0.7, 0.8), color: '#444', lineHeight: 1.5 }}>
                    {manageDetails.message}
                  </div>

                  <div style={{ display: 'flex', gap: scaledBookingRem(0.5, 0.7), flexWrap: 'wrap' }}>
                    {manageDetails.canCancel && (
                      <button
                        type="button"
                        onClick={handleCancelManagedAppointment}
                        disabled={isManageActionSubmitting}
                        style={{
                          background: '#FFF1F2',
                          color: '#B42318',
                          border: '1px solid #FECACA',
                          padding: `${scaledBookingRem(0.62, 0.75)} ${scaledBookingRem(0.75, 1)}`,
                          borderRadius: scaledBookingRem(0.5, 0.6),
                          fontSize: scaledBookingRem(0.72, 0.8),
                          fontWeight: 700,
                          cursor: isManageActionSubmitting ? 'wait' : 'pointer',
                        }}
                      >
                        {isManageActionSubmitting ? 'Cancelling…' : 'Cancel Appointment'}
                      </button>
                    )}
                    {manageDetails.canReschedule && (
                      <button
                        type="button"
                        onClick={startRescheduleFlow}
                        style={{
                          background: activeColorScheme.PRIMARY,
                          color: '#FFF',
                          border: 'none',
                          padding: `${scaledBookingRem(0.62, 0.75)} ${scaledBookingRem(0.75, 1)}`,
                          borderRadius: scaledBookingRem(0.5, 0.6),
                          fontSize: scaledBookingRem(0.72, 0.8),
                          fontWeight: 700,
                          cursor: 'pointer',
                        }}
                      >
                        Choose New Time
                      </button>
                    )}
                    {!manageDetails.canCancel && !manageDetails.canReschedule && (
                      <button
                        type="button"
                        onClick={() => setShowManageModal(false)}
                        style={{
                          background: activeColorScheme.PRIMARY,
                          color: '#FFF',
                          border: 'none',
                          padding: `${scaledBookingRem(0.62, 0.75)} ${scaledBookingRem(0.75, 1)}`,
                          borderRadius: scaledBookingRem(0.5, 0.6),
                          fontSize: scaledBookingRem(0.72, 0.8),
                          fontWeight: 700,
                          cursor: 'pointer',
                        }}
                      >
                        Close
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {step === 'services' && (
          <div style={{ textAlign: 'center' }}>
            <h1
              style={{
                fontSize: bookingIntroTitleSize,
                fontWeight: 700,
                marginBottom: '0.35rem',
                fontFamily: activeFontTheme?.HEADING,
                color: activeColorScheme.PRIMARY,
                lineHeight: 1.2,
                overflowWrap: 'anywhere',
              }}
            >
              {business?.name ? `Book with ${business.name}` : 'Services'}
            </h1>
            <p style={{
              color: '#666',
              margin: '0 auto 1.1rem',
              maxWidth: '640px',
              fontSize: bookingIntroCopySize,
              lineHeight: 1.5,
              overflowWrap: 'anywhere',
            }}>
              {business?.name
                ? 'Pick a service and choose from the next available times.'
                : 'Pick a service and choose from the next available times.'}
            </p>
            {screen.isMobile ? (
              <ScrollableContainer
                colorScheme={activeColorScheme}
                style={{ maxHeight: '70vh', margin: '0 auto', paddingBottom: '0.5rem', width: '100%', maxWidth: '420px' }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {services.map((service) => (
                    <ServiceCard
                      key={service.id}
                      service={service}
                      colorScheme={activeColorScheme}
                      selected={selectedServiceId === service.id}
                      onSelect={() => handleSelectService(service)}
                      screenWidth={screen.width}
                    />
                  ))}
                </div>
              </ScrollableContainer>
            ) : (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                  gap: '1.2rem',
                  maxWidth: '920px',
                  margin: '0 auto',
                  justifyContent: 'center',
                  padding: useTabletLikeInsetLayout ? '0 0.35rem' : undefined,
                  boxSizing: 'border-box',
                }}
              >
                {services.map((service) => (
                  <ServiceCard
                    key={service.id}
                    service={service}
                    colorScheme={activeColorScheme}
                    selected={selectedServiceId === service.id}
                    onSelect={() => handleSelectService(service)}
                    screenWidth={screen.width}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {step === 'time' && selectedService && (
          (screen.isMobile || useTouchStyleBookingFlow) ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: scaledBookingRem(0.75, 1.25) }}>
              {!showMobileCalendar && selectedDate ? (
                <div
                  style={{
                    ...cardBase,
                    padding: !showMobileCalendar && selectedDate ? '0.62rem 0.72rem' : '0.75rem 1rem',
                    position: useStickyMobileSummary ? 'sticky' : 'relative',
                    top: useStickyMobileSummary ? 0 : undefined,
                    zIndex: useStickyMobileSummary ? 20 : undefined,
                    background: '#FFF',
                    gap: !showMobileCalendar && selectedDate ? '0.34rem' : '0.5rem',
                    justifyContent: !showMobileCalendar && selectedDate ? 'center' : undefined,
                  }}
                >
                  <button
                    onClick={() => {
                      if (!isServiceLocked) {
                        setStep('services');
                      }
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      width: '100%',
                      cursor: isServiceLocked ? 'default' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      padding: 0,
                      minHeight: !showMobileCalendar && selectedDate ? '1.7rem' : undefined,
                      position: 'relative',
                      justifyContent: 'center',
                      minWidth: 0,
                    }}
                  >
                    <span
                      style={{
                        fontSize: !showMobileCalendar && selectedDate ? '0.9rem' : '0.85rem',
                        fontWeight: 700,
                        color: activeColorScheme.PRIMARY,
                        textAlign: 'center',
                        flex: '0 1 auto',
                        minWidth: 0,
                        lineHeight: 1.15,
                      }}
                    >
                      {selectedService.name}
                    </span>
                    <span
                      style={{
                        fontSize: '1rem',
                        position: 'absolute',
                        right: 0,
                        top: '50%',
                        transformOrigin: 'center',
                        translate: '0 -50%',
                      }}
                    >
                      ›
                    </span>
                  </button>
                  {showMobileCalendar && showMobileService && (
                    <div style={{ marginTop: '0.6rem', display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
                      <div style={{ fontSize: '0.7rem', color: '#444' }}>{selectedService.description}</div>
                      <div style={{ fontSize: '0.65rem', fontWeight: 600 }}>Duration: {selectedService.durationMinutes} mins</div>
                      <div style={{ fontSize: '0.7rem', fontWeight: 700 }}>
                        {selectedService.currency} ${selectedService.price}
                      </div>
                    </div>
                  )}
                  {showMobileCalendar && !isServiceLocked && (
                    <button
                      onClick={() => setStep('services')}
                      style={{
                        marginTop: '0.75rem',
                        background: 'transparent',
                        border: 'none',
                        color: '#444',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        fontSize: '0.65rem',
                      }}
                    >
                      Change Service
                    </button>
                  )}
                </div>
              ) : (
                <div style={{
                  ...cardBase,
                  padding: '0.75rem 1rem',
                  position: useStickyMobileSummary ? 'sticky' : 'relative',
                  top: useStickyMobileSummary ? 0 : undefined,
                  zIndex: useStickyMobileSummary ? 20 : undefined,
                  background: '#FFF',
                  gap: '0.5rem',
                }}>
                  <button
                    onClick={() => setShowMobileService(!showMobileService)}
                    style={{
                      background: 'none',
                      border: 'none',
                      width: '100%',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      padding: 0,
                      position: 'relative',
                      justifyContent: 'center',
                    }}
                  >
                    <span
                      style={{
                        fontSize: scaledBookingRem(0.72, 0.85),
                        fontWeight: 700,
                        color: activeColorScheme.PRIMARY,
                        textAlign: 'center',
                        flex: '0 1 auto',
                      }}
                    >
                      {selectedService.name}
                    </span>
                    <span
                      style={{
                        fontSize: '1rem',
                        transform: showMobileService ? 'rotate(90deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s',
                        position: 'absolute',
                        right: 0,
                        top: '50%',
                        transformOrigin: 'center',
                        translate: '0 -50%',
                      }}
                    >
                      ›
                    </span>
                  </button>
                  {showMobileService && (
                    <div style={{ marginTop: '0.6rem', display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
                      <div style={{ fontSize: '0.7rem', color: '#444' }}>{selectedService.description}</div>
                      <div style={{ fontSize: '0.65rem', fontWeight: 600 }}>Duration: {selectedService.durationMinutes} mins</div>
                      <div style={{ fontSize: '0.7rem', fontWeight: 700 }}>
                        {selectedService.currency} ${selectedService.price}
                      </div>
                    </div>
                  )}
                  {!isServiceLocked && (
                    <button
                      onClick={() => setStep('services')}
                      style={{
                        marginTop: '0.75rem',
                        background: 'transparent',
                        border: 'none',
                        color: '#444',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        fontSize: '0.65rem',
                      }}
                    >
                      Change Service
                    </button>
                  )}
                </div>
              )}
              {showMobileCalendar || !selectedDate ? (
                <div>
                  {renderCalendarPanel()}
                </div>
              ) : null}
              <div>
                <h2
                  style={{
                    fontSize: scaledBookingRem(0.72, 0.85),
                    fontWeight: 700,
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    marginBottom: scaledBookingRem(0.4, 0.6),
                    color: '#555',
                  }}
                >
                  Pick a Time
                </h2>
                {availabilityError && <div style={{ color: '#B00020', marginBottom: '0.75rem' }}>{availabilityError}</div>}
                {showMonthAvailabilityLoadingState && <div style={{ color: '#666', marginBottom: '0.65rem' }}>Loading this month&apos;s live availability...</div>}
                <TimePicker
                  selectedDate={selectedDate}
                  selectedPeriod={selectedPeriod}
                  setSelectedPeriod={(period) => {
                    setSelectedPeriod(period);
                    setSelectedTime(null);
                  }}
                  selectedTime={selectedTime ? formatDisplayTime(selectedTime) : null}
                  onSelectTime={proceedToOverview}
                  colorScheme={activeColorScheme}
                  periodTimes={periodTimes}
                  layoutWidth={screen.width}
                  compactSelectedDayActions={!showMobileCalendar && selectedDate ? {
                    onChangeDay: handleChooseDifferentDay,
                    onNextDay: handleSelectNextAvailableDay,
                    nextDayEnabled: Boolean(nextAvailableDate),
                  } : undefined}
                />
              </div>
            </div>
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: useCompactTimeStepLayout
                  ? 'minmax(0, 1fr)'
                  : useCombinedCalendarTimeColumn
                    ? 'minmax(220px, 260px) minmax(0, 1fr)'
                  : useTabletThreeColumnTimeLayout
                    ? 'minmax(150px, 180px) minmax(0, 1.2fr) minmax(220px, 0.95fr)'
                    : (screen.isTablet ? '240px 1fr' : '280px 1fr 300px'),
                gap: useTabletThreeColumnTimeLayout ? '0.9rem' : '1.5rem',
                alignItems: 'start',
              }}
            >
              <div style={{ padding: useTabletLikeInsetLayout ? '0 0.3rem' : undefined }}>
                <h2
                  style={{
                    fontSize: scaledBookingRem(0.72, 0.85),
                    fontWeight: 700,
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    marginBottom: scaledBookingRem(0.4, 0.6),
                    color: '#555',
                  }}
                >
                  Your Chosen Service
                </h2>
                <ServiceCard
                  service={selectedService}
                  hideButton
                  colorScheme={activeColorScheme}
                  screenWidth={screen.width}
                  selected
                  compact={useTabletThreeColumnTimeLayout}
                />
                {!isServiceLocked && (
                  <button
                    onClick={() => setStep('services')}
                    style={{
                      marginTop: '0.8rem',
                      background: 'transparent',
                      border: 'none',
                      color: '#444',
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      fontSize: '0.65rem',
                    }}
                  >
                    Change Service
                  </button>
                )}
              </div>
              {useCombinedCalendarTimeColumn ? (
                <div style={{ padding: useTabletLikeInsetLayout ? '0 0.3rem' : undefined, display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div>
                    {renderCalendarPanel()}
                  </div>
                  <div>
                    <h2
                      style={{
                        fontSize: scaledBookingRem(0.72, 0.85),
                        fontWeight: 700,
                        letterSpacing: '0.5px',
                        textTransform: 'uppercase',
                        marginBottom: scaledBookingRem(0.4, 0.6),
                        color: '#555',
                      }}
                    >
                      Pick a Time
                    </h2>
                    {availabilityError && <div style={{ color: '#B00020', marginBottom: '0.75rem' }}>{availabilityError}</div>}
                    {showMonthAvailabilityLoadingState && <div style={{ color: '#666', marginBottom: '0.65rem' }}>Loading this month&apos;s live availability...</div>}
                    <TimePicker
                      selectedDate={selectedDate}
                      selectedPeriod={selectedPeriod}
                      setSelectedPeriod={(period) => {
                        setSelectedPeriod(period);
                        setSelectedTime(null);
                      }}
                      selectedTime={selectedTime ? formatDisplayTime(selectedTime) : null}
                      onSelectTime={proceedToOverview}
                      colorScheme={activeColorScheme}
                      periodTimes={periodTimes}
                      layoutWidth={screen.width >= 900 ? 360 : 320}
                    />
                  </div>
                </div>
              ) : (
                <>
                  <div style={{ padding: useTabletLikeInsetLayout ? '0 0.3rem' : undefined }}>
                    {renderCalendarPanel()}
                  </div>
                  <div style={{ padding: useTabletLikeInsetLayout ? '0 0.3rem' : undefined }}>
                    <h2
                      style={{
                        fontSize: scaledBookingRem(0.72, 0.85),
                        fontWeight: 700,
                        letterSpacing: '0.5px',
                        textTransform: 'uppercase',
                        marginBottom: scaledBookingRem(0.4, 0.6),
                        color: '#555',
                      }}
                    >
                      Pick a Time
                    </h2>
                    {availabilityError && <div style={{ color: '#B00020', marginBottom: '0.75rem' }}>{availabilityError}</div>}
                    {showMonthAvailabilityLoadingState && <div style={{ color: '#666', marginBottom: '0.65rem' }}>Loading this month&apos;s live availability...</div>}
                    <TimePicker
                      selectedDate={selectedDate}
                      selectedPeriod={selectedPeriod}
                      setSelectedPeriod={(period) => {
                        setSelectedPeriod(period);
                        setSelectedTime(null);
                      }}
                      selectedTime={selectedTime ? formatDisplayTime(selectedTime) : null}
                      onSelectTime={proceedToOverview}
                      colorScheme={activeColorScheme}
                      periodTimes={periodTimes}
                      layoutWidth={useTabletThreeColumnTimeLayout ? 300 : screen.width}
                    />
                  </div>
                </>
              )}
            </div>
          )
        )}

        {(step === 'overview' || step === 'deposit') && selectedService && selectedDate && selectedTime && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <h1
              style={{
                fontSize: scaledBookingRem(1, 1.3),
                fontWeight: 700,
                marginBottom: scaledBookingRem(0.6, 1),
                fontFamily: activeFontTheme?.HEADING,
                color: activeColorScheme.PRIMARY,
                textAlign: 'center',
              }}
            >
              Appointment Overview
            </h1>
            <div
              style={{
                ...cardBase,
                position: screen.isMobile && useStickyMobileSummary ? 'sticky' : 'relative',
                top: screen.isMobile && useStickyMobileSummary ? (previewMode ? '0.35rem' : '5.25rem') : undefined,
                zIndex: screen.isMobile && useStickyMobileSummary ? 20 : undefined,
                width: '100%',
                maxWidth: overviewPanelMaxWidth,
                margin: '0 auto 1rem',
                padding: scaledBookingRem(0.65, 1.1),
                background: '#FFFFFFEE',
                backdropFilter: 'blur(10px)',
                border: `1px solid ${activeColorScheme.PRIMARY}24`,
                boxShadow: '0 0.85rem 1.9rem rgba(15, 23, 42, 0.08)',
              }}
            >
              <div style={{ fontSize: scaledBookingRem(0.58, 0.68), fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: activeColorScheme.PRIMARY }}>
                Booking summary
              </div>
              <div style={{ marginTop: scaledBookingRem(0.25, 0.35), display: 'grid', gridTemplateColumns: screen.isMobile ? '1fr' : 'minmax(0, 1.1fr) minmax(0, 0.9fr)', gap: scaledBookingRem(0.55, 0.9) }}>
                <div>
                  <div style={{ fontSize: scaledBookingRem(0.84, 1), fontWeight: 800, color: '#111827' }}>{selectedService.name}</div>
                  <div style={{ marginTop: scaledBookingRem(0.2, 0.3), fontSize: scaledBookingRem(0.68, 0.78), color: '#4B5563', lineHeight: 1.5 }}>
                    {selectedService.description}
                  </div>
                  <div style={{ marginTop: scaledBookingRem(0.4, 0.55), display: 'flex', gap: scaledBookingRem(0.3, 0.45), flexWrap: 'wrap' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', borderRadius: '999px', background: '#F6F1FF', color: activeColorScheme.PRIMARY, padding: `${scaledBookingRem(0.22, 0.32)} ${scaledBookingRem(0.42, 0.6)}`, fontSize: scaledBookingRem(0.62, 0.7), fontWeight: 700 }}>
                      {selectedService.durationMinutes} min
                    </span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', borderRadius: '999px', background: '#FFF7ED', color: '#B45309', padding: `${scaledBookingRem(0.22, 0.32)} ${scaledBookingRem(0.42, 0.6)}`, fontSize: scaledBookingRem(0.62, 0.7), fontWeight: 800 }}>
                      {formatCurrency(selectedService.currency, selectedService.price)}
                    </span>
                  </div>
                </div>
                <div style={{ display: 'grid', gap: scaledBookingRem(0.4, 0.55) }}>
                  <div style={{ borderRadius: scaledBookingRem(0.7, 0.9), background: '#F8FAFC', padding: `${scaledBookingRem(0.55, 0.8)} ${scaledBookingRem(0.6, 0.9)}`, textAlign: 'center', minHeight: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <div style={{ fontSize: scaledBookingRem(0.56, 0.64), fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6B7280' }}>When</div>
                    <div style={{ marginTop: scaledBookingRem(0.18, 0.25), fontSize: scaledBookingRem(0.8, 0.95), fontWeight: 800, color: '#111827' }}>{formattedOverviewDate}</div>
                    <div style={{ marginTop: '0.12rem', fontSize: scaledBookingRem(0.7, 0.82), color: '#4B5563' }}>{selectedTimeLabel}</div>
                  </div>
                </div>
              </div>
            </div>
            <form
              noValidate
              onSubmit={submit}
              style={{
                ...cardBase,
                width: '100%',
                maxWidth: overviewPanelMaxWidth,
                margin: '0 auto',
                padding: `${scaledBookingRem(0.85, 2.2)} ${scaledBookingRem(0.7, 2)} ${scaledBookingRem(1.1, 2.4)}`,
                boxSizing: 'border-box',
                overflow: 'hidden',
              }}
            >
              {step === 'deposit' && bookingData ? (
                /* Deposit step — shows confirmation interstitial first, then Stripe form in-place */
                pendingDepositResponse?.requiresPayment && depositStripePromise && depositElementsOptions ? (
                  /* Stripe payment form — same card, replaces the interstitial once slot is reserved */
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', padding: screen.isMobile ? '0.25rem 0' : '0.5rem 0' }}>
                    <div>
                      <div style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', color: activeColorScheme.PRIMARY, marginBottom: '0.35rem' }}>
                        Payment
                      </div>
                      <div style={{ fontSize: screen.isMobile ? '1rem' : '1.1rem', fontWeight: 700, marginBottom: '0.25rem' }}>
                        Pay ${(pendingDepositResponse.paymentDetails?.depositAmount ?? estimatedDepositDueToday).toFixed(2)} to reserve this appointment
                      </div>
                      <div style={{ fontSize: '0.76rem', color: '#555', lineHeight: 1.5 }}>
                        Your time is temporarily held while you complete the deposit.
                        Secure checkout is powered by Stripe.
                      </div>
                    </div>
                    {submitError && <div style={{ color: '#B00020', fontSize: '0.8rem' }}>{submitError}</div>}
                    <Elements stripe={depositStripePromise} options={depositElementsOptions}>
                      <DepositPaymentForm
                        onSubmitted={finalizeDepositAppointment}
                        isSaving={isFinalizingDeposit}
                        accentColor={activeColorScheme.PRIMARY}
                        layoutWidth={screen.width}
                        returnUrl={`${window.location.origin}${window.location.pathname}`}
                      />
                    </Elements>
                    <button
                      type="button"
                      onClick={() => { void abandonPendingDeposit(); }}
                      disabled={isAbandoningDeposit || isFinalizingDeposit}
                      style={{
                        background: 'transparent', color: activeColorScheme.PRIMARY,
                        border: `1px solid ${activeColorScheme.PRIMARY}`,
                        padding: '0.68rem 1.1rem', borderRadius: '0.6rem',
                        fontSize: '0.82rem', fontWeight: 600,
                        cursor: isAbandoningDeposit || isFinalizingDeposit ? 'wait' : 'pointer',
                        opacity: isAbandoningDeposit || isFinalizingDeposit ? 0.7 : 1,
                        width: '100%',
                      }}
                    >
                      {isAbandoningDeposit ? 'Releasing time…' : 'Go back'}
                    </button>
                  </div>
                ) : (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.1rem', textAlign: 'center', padding: screen.isMobile ? '0.5rem 0' : '0.75rem 0' }}>
                  <div style={{
                    width: '3rem', height: '3rem', borderRadius: '999px',
                    background: `${activeColorScheme.PRIMARY}15`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '1.4rem',
                    fontWeight: 800,
                    color: activeColorScheme.PRIMARY,
                  }}>
                    !
                  </div>
                  <div>
                    <div style={{ fontSize: screen.isMobile ? '1rem' : '1.15rem', fontWeight: 800, color: activeColorScheme.PRIMARY, marginBottom: '0.4rem' }}>
                      Deposit required to book
                    </div>
                    <div style={{ fontSize: '0.88rem', color: '#444', lineHeight: 1.65, maxWidth: '21rem', margin: '0 auto' }}>
                      <strong>{bookingData.business.name}</strong> requires a{' '}
                      <strong>${estimatedDepositDueToday.toFixed(2)} deposit</strong>{' '}
                      to secure your appointment.{' '}
                      {publicPaymentSummary?.isRefundable
                        ? `The deposit is refundable when canceled at least ${publicPaymentSummary.refundWindowHours} hour${publicPaymentSummary.refundWindowHours === 1 ? '' : 's'} before your appointment.`
                        : 'This deposit is non-refundable.'}
                    </div>
                  </div>
                  {submitError && <div style={{ color: '#B00020', fontSize: '0.8rem' }}>{submitError}</div>}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.55rem', width: '100%', maxWidth: '17rem' }}>
                    <button
                      type="button"
                      onClick={() => { void confirmDepositAndBook(); }}
                      disabled={isSubmitting}
                      style={{
                        background: activeColorScheme.PRIMARY, color: '#FFF', border: 'none',
                        padding: '0.78rem 1.1rem', borderRadius: '0.6rem',
                        fontSize: '0.88rem', fontWeight: 700,
                        cursor: isSubmitting ? 'wait' : 'pointer',
                        opacity: isSubmitting ? 0.7 : 1, width: '100%',
                      }}
                    >
                      {isSubmitting ? 'Processing…' : `Pay $${estimatedDepositDueToday.toFixed(2)} and Reserve`}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setStep('overview'); setSubmitError(''); }}
                      style={{
                        background: 'transparent', color: activeColorScheme.PRIMARY,
                        border: `1px solid ${activeColorScheme.PRIMARY}`,
                        padding: '0.68rem 1.1rem', borderRadius: '0.6rem',
                        fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', width: '100%',
                      }}
                    >
                      Go back
                    </button>
                  </div>
                </div>
                )
              ) : (
              <>
              <div style={{ fontSize: scaledBookingRem(0.66, 0.75), fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', color: '#777' }}>
                {isServiceLocked ? 'Client Info On File' : 'Client Info'}
              </div>
              <div style={{ fontSize: scaledBookingRem(0.68, 0.78), color: '#6B7280', lineHeight: 1.5 }}>
                {isServiceLocked
                  ? 'We kept the original client details so you only need to confirm the new time.'
                  : 'A few quick details and your appointment will be ready to go.'}
              </div>
              {isServiceLocked && (
                <div style={{ fontSize: '0.72rem', color: '#555', lineHeight: 1.5 }}>
                  We&apos;re keeping the original client details for this reschedule. You&apos;re only choosing a new appointment time.
                </div>
              )}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: screen.isMobile ? '1fr' : 'repeat(2, 1fr)',
                  gap: scaledBookingRem(0.65, 1),
                  width: '100%',
                }}
              >
                {[
                  { label: 'First Name', value: firstName, setter: setFirstName, required: true, type: 'text' },
                  { label: 'Last Name', value: lastName, setter: setLastName, required: true, type: 'text' },
                  { label: 'Email', value: email, setter: setEmail, required: true, type: 'email' },
                  { label: 'Phone', value: phone, setter: setPhone, required: false, type: 'tel' },
                ].map((field) => (
                  <div key={field.label} style={{ display: 'flex', flexDirection: 'column', gap: scaledBookingRem(0.25, 0.35) }}>
                    <label
                      style={{
                        fontSize: scaledBookingRem(0.56, 0.64),
                        fontWeight: 600,
                        letterSpacing: '0.5px',
                        textTransform: 'uppercase',
                      }}
                    >
                      {field.label}
                      {field.label === 'Email' && (
                        <span style={{ color: '#B00020', marginLeft: '0.35rem', fontWeight: 600 }}>(required)</span>
                      )}
                    </label>
                    <input
                      type={field.type}
                      required={field.required}
                      value={field.value}
                      disabled={isServiceLocked}
                      onChange={(inputEvent) => field.setter(inputEvent.target.value)}
                      onBlur={field.label === 'Email' ? () => setEmailTouched(true) : undefined}
                      placeholder={field.label === 'Phone' ? '(555) 123-4567' : undefined}
                      style={{
                        border: field.label === 'Email' && emailTouched && !email.trim()
                          ? '1px solid #B00020'
                          : '1px solid #CCC',
                        borderRadius: scaledBookingRem(0.42, 0.5),
                        padding: scaledBookingRem(0.48, 0.6),
                        fontSize: scaledBookingRem(0.66, 0.75),
                        background: isServiceLocked ? '#F6F6F6' : '#FFF',
                        color: isServiceLocked ? '#666' : '#222',
                      }}
                    />
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: scaledBookingRem(0.25, 0.35) }}>
                <label style={{ fontSize: scaledBookingRem(0.56, 0.64), fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                  Additional Notes
                  <span style={{ color: '#999', marginLeft: '0.3rem', fontWeight: 400, textTransform: 'none', fontSize: '0.58rem' }}>(optional)</span>
                </label>
                <textarea
                  value={clientNotes}
                  maxLength={500}
                  disabled={isServiceLocked}
                  onChange={(e) => setClientNotes(e.target.value)}
                  placeholder="E.g. address, special requests, anything your provider should know…"
                  rows={3}
                  style={{
                    border: '1px solid #CCC',
                    borderRadius: scaledBookingRem(0.42, 0.5),
                    padding: scaledBookingRem(0.48, 0.6),
                    fontSize: scaledBookingRem(0.66, 0.75),
                    background: isServiceLocked ? '#F6F6F6' : '#FFF',
                    color: isServiceLocked ? '#666' : '#222',
                    resize: 'vertical',
                    fontFamily: 'inherit',
                  }}
                />
                {clientNotes.length > 0 && (
                  <div style={{ fontSize: '0.58rem', color: clientNotes.length >= 480 ? '#B00020' : '#999', textAlign: 'right' }}>
                    {clientNotes.length}/500
                  </div>
                )}
              </div>
              <div
                style={{
                  marginTop: '0.35rem',
                  borderRadius: scaledBookingRem(0.7, 0.9),
                  border: '1px solid #E5E7EB',
                  background: '#FAFBFF',
                  padding: `${scaledBookingRem(0.65, 0.9)} ${scaledBookingRem(0.65, 0.95)}`,
                }}
              >
                <div style={{ fontSize: scaledBookingRem(0.64, 0.72), fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: activeColorScheme.PRIMARY }}>
                  Stay in the loop
                </div>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: scaledBookingRem(0.38, 0.5), marginTop: scaledBookingRem(0.5, 0.7), fontSize: scaledBookingRem(0.62, 0.68), textAlign: 'left', width: '100%', lineHeight: 1.35 }}>
                  <input
                    type="checkbox"
                    checked={marketingConsent}
                    disabled={isServiceLocked}
                    onChange={(inputEvent) => setMarketingConsent(inputEvent.target.checked)}
                    style={{ width: '18px', height: '18px', accentColor: activeColorScheme.PRIMARY }}
                  />
                  I consent to receive marketing & promotional communications via email and other non-SMS channels.
                </label>
                {smsNotificationsEnabled ? (
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: scaledBookingRem(0.38, 0.5), marginTop: scaledBookingRem(0.48, 0.65), fontSize: scaledBookingRem(0.62, 0.68), textAlign: 'left', width: '100%', lineHeight: 1.35 }}>
                    <input
                      type="checkbox"
                      checked={smsConsent}
                      disabled={isServiceLocked}
                      onChange={(inputEvent) => setSmsConsent(inputEvent.target.checked)}
                      style={{ width: '18px', height: '18px', accentColor: activeColorScheme.PRIMARY }}
                    />
                    <span>
                      I agree to receive appointment confirmations, cancellations, and reminders by text from LoviBook and this business. Message frequency varies. Message and data rates may apply. Reply STOP to opt out.{' '}
                      <a
                        href={`${PUBLIC_SITE_ORIGIN}/terms-of-service`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(event) => event.stopPropagation()}
                        style={{ color: activeColorScheme.PRIMARY, fontWeight: 700 }}
                      >
                        Terms of Service
                      </a>
                      {' · '}
                      <a
                        href={`${PUBLIC_SITE_ORIGIN}/privacy-policy`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(event) => event.stopPropagation()}
                        style={{ color: activeColorScheme.PRIMARY, fontWeight: 700 }}
                      >
                        Privacy Policy
                      </a>
                    </span>
                  </label>
                ) : null}
              </div>
              {depositReadyForBooking && selectedService ? (
                <div
                  style={{
                    marginTop: '0.65rem',
                    padding: `${scaledBookingRem(0.65, 0.9)} ${scaledBookingRem(0.7, 1)}`,
                    borderRadius: scaledBookingRem(0.6, 0.75),
                    border: `1px solid ${activeColorScheme.PRIMARY}`,
                    background: `${activeColorScheme.PRIMARY}10`,
                    textAlign: 'left',
                  }}
                >
                  <div style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', color: activeColorScheme.PRIMARY }}>
                    Deposit due today
                  </div>
                  <div style={{ marginTop: '0.35rem', fontSize: '1.05rem', fontWeight: 700 }}>
                    ${estimatedDepositDueToday.toFixed(2)}
                  </div>
                  <div style={{ marginTop: '0.25rem', fontSize: '0.74rem', color: '#555', lineHeight: 1.45 }}>
                    {publicPaymentSummary?.depositType === DepositType.PERCENTAGE
                      ? `${publicPaymentSummary.depositValue}% of the $${selectedService.price.toFixed(2)} service total will be charged now.`
                      : `A fixed $${estimatedDepositDueToday.toFixed(2)} deposit will be charged now.`}
                  </div>
                </div>
              ) : null}
              {submitError && <div style={{ color: '#B00020', fontSize: '0.75rem' }}>{submitError}</div>}
              <div style={{ display: 'flex', gap: scaledBookingRem(0.45, 0.65), marginTop: scaledBookingRem(0.7, 1), flexWrap: 'wrap', justifyContent: 'center' }}>
                <button
                  type="submit"
                  disabled={isSubmitting || Boolean(pendingDepositResponse?.requiresPayment)}
                  style={{
                    background: activeColorScheme.PRIMARY,
                    color: '#FFF',
                    border: 'none',
                    padding: `${scaledBookingRem(0.62, 0.75)} ${scaledBookingRem(0.9, 1.35)}`,
                    borderRadius: scaledBookingRem(0.5, 0.6),
                    fontSize: scaledBookingRem(0.72, 0.8),
                    fontWeight: 700,
                    cursor: isSubmitting || pendingDepositResponse?.requiresPayment ? 'wait' : 'pointer',
                    opacity: isSubmitting || pendingDepositResponse?.requiresPayment ? 0.7 : 1,
                  }}
                >
                  {isSubmitting
                    ? (isServiceLocked ? 'Rescheduling…' : 'Confirming…')
                    : previewMode
                      ? 'Preview Confirmation'
                      : isServiceLocked
                        ? 'Reschedule Appointment'
                        : depositReadyForBooking
                          ? 'Review and Pay Deposit'
                          : 'Book Appointment'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    clearPendingDeposit();
                    setStep('time');
                  }}
                  style={{
                    background: 'transparent',
                    color: activeColorScheme.PRIMARY,
                    border: `1px solid ${activeColorScheme.PRIMARY}`,
                    padding: `${scaledBookingRem(0.62, 0.75)} ${scaledBookingRem(0.9, 1.35)}`,
                    borderRadius: scaledBookingRem(0.5, 0.6),
                    fontSize: scaledBookingRem(0.72, 0.8),
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  Back
                </button>
              </div>
              </>
              )}
            </form>
          </div>
        )}

        {step === 'confirmed' && selectedService && selectedDate && selectedTime && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: scaledBookingRem(0.8, 1.15), padding: `${scaledBookingRem(0.9, 2)} ${scaledBookingRem(0, 1)}` }}>
            <div
              style={{
                ...cardBase,
                maxWidth: '680px',
                width: '100%',
                textAlign: 'center',
                border: `2px solid ${activeColorScheme.PRIMARY}`,
                padding: `${scaledBookingRem(1, 1.8)} ${scaledBookingRem(0.75, 1.8)} ${scaledBookingRem(1.1, 1.9)}`,
                boxShadow: `0 1rem 2.2rem ${activeColorScheme.PRIMARY}18`,
              }}
            >
              <div
                style={{
                  width: scaledBookingRem(3, 4),
                  height: scaledBookingRem(3, 4),
                  margin: '0 auto',
                  borderRadius: '999px',
                  background: `linear-gradient(135deg, ${activeColorScheme.PRIMARY} 0%, ${activeColorScheme.ACCENT || activeColorScheme.PRIMARY} 100%)`,
                  color: '#FFFFFF',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: scaledBookingRem(1.45, 1.9),
                  fontWeight: 800,
                  boxShadow: `0 0.9rem 1.7rem ${activeColorScheme.PRIMARY}30`,
                }}
              >
                ✓
              </div>
              <h2 style={{ margin: `${scaledBookingRem(0.7, 0.95)} 0 0`, fontSize: scaledBookingRem(1.15, 1.65), fontWeight: 800, color: activeColorScheme.PRIMARY }}>
                {previewMode ? 'Preview Complete' : confirmationMessage ? 'Appointment Updated' : "You're booked!"}
              </h2>
              <div style={{ marginTop: scaledBookingRem(0.3, 0.45), fontSize: scaledBookingRem(0.72, 0.84), color: '#4B5563', lineHeight: 1.55 }}>
                {previewMode
                  ? 'This is a sample confirmation view shown in preview mode.'
                  : confirmationMessage || `We can't wait to see you. ${business.name} has your appointment details ready to go.`}
              </div>

              <div
                style={{
                  marginTop: scaledBookingRem(0.7, 1),
                  display: 'grid',
                  gridTemplateColumns: screen.isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))',
                  gap: scaledBookingRem(0.55, 0.8),
                  textAlign: 'left',
                }}
              >
                <div style={{ borderRadius: scaledBookingRem(0.75, 1), background: '#F8FAFC', padding: `${scaledBookingRem(0.7, 0.95)} ${scaledBookingRem(0.7, 1)}` }}>
                  <div style={{ fontSize: scaledBookingRem(0.6, 0.68), fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6B7280' }}>
                    Appointment
                  </div>
                  <div style={{ marginTop: scaledBookingRem(0.22, 0.3), fontSize: scaledBookingRem(0.84, 1), fontWeight: 800, color: '#111827' }}>{selectedService.name}</div>
                  <div style={{ marginTop: scaledBookingRem(0.25, 0.35), fontSize: scaledBookingRem(0.7, 0.8), color: '#4B5563', lineHeight: 1.55 }}>
                    {selectedDate.toLocaleString('default', { weekday: 'long', month: 'short', day: 'numeric' })} at {selectedTimeLabel}
                  </div>
                  <div style={{ marginTop: scaledBookingRem(0.32, 0.45), display: 'flex', flexWrap: 'wrap', gap: scaledBookingRem(0.3, 0.45) }}>
                    <span style={{ display: 'inline-flex', borderRadius: '999px', background: '#FFFFFF', padding: `${scaledBookingRem(0.22, 0.3)} ${scaledBookingRem(0.42, 0.58)}`, fontSize: scaledBookingRem(0.61, 0.68), fontWeight: 700, color: activeColorScheme.PRIMARY }}>
                      {selectedService.durationMinutes} min
                    </span>
                    <span style={{ display: 'inline-flex', borderRadius: '999px', background: '#FFFFFF', padding: `${scaledBookingRem(0.22, 0.3)} ${scaledBookingRem(0.42, 0.58)}`, fontSize: scaledBookingRem(0.61, 0.68), fontWeight: 800, color: '#B45309' }}>
                      {formatCurrency(selectedService.currency, selectedService.price)}
                    </span>
                  </div>
                </div>
                <div style={{ borderRadius: scaledBookingRem(0.75, 1), background: `${activeColorScheme.PRIMARY}10`, padding: `${scaledBookingRem(0.7, 0.95)} ${scaledBookingRem(0.7, 1)}` }}>
                  <div style={{ fontSize: scaledBookingRem(0.6, 0.68), fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: activeColorScheme.PRIMARY }}>
                    What happens next
                  </div>
                  <div style={{ marginTop: scaledBookingRem(0.22, 0.3), fontSize: scaledBookingRem(0.7, 0.8), color: '#4B5563', lineHeight: 1.6 }}>
                  A confirmation email should arrive shortly, and any enabled reminders will follow automatically.
                  </div>
                  <div style={{ marginTop: scaledBookingRem(0.4, 0.55), fontSize: scaledBookingRem(0.69, 0.78), fontWeight: 700, color: '#111827' }}>
                    We can&apos;t wait to see you.
                  </div>
                </div>
              </div>

              <div
                style={{
                  marginTop: scaledBookingRem(0.7, 1),
                  display: 'grid',
                  gridTemplateColumns: screen.isMobile
                    ? '1fr'
                    : `repeat(${(businessAddress ? 1 : 0) + (customerContactDetails ? 1 : 0) + (appointmentInstructions ? 1 : 0)}, minmax(0, 1fr))`,
                  gap: scaledBookingRem(0.5, 0.75),
                  textAlign: 'left',
                }}
              >
                {customerContactDetails ? (
                  <div style={{ borderRadius: scaledBookingRem(0.72, 0.95), border: '1px solid #E5E7EB', padding: `${scaledBookingRem(0.62, 0.85)} ${scaledBookingRem(0.65, 0.95)}` }}>
                    <div style={{ fontSize: scaledBookingRem(0.58, 0.66), fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6B7280' }}>
                      Contact
                    </div>
                    <div style={{ marginTop: scaledBookingRem(0.22, 0.3), fontSize: scaledBookingRem(0.7, 0.8), color: '#111827', lineHeight: 1.55 }}>
                      {customerContactDetails.summary}
                    </div>
                  </div>
                ) : null}
                {businessAddress ? (
                  <div style={{ borderRadius: scaledBookingRem(0.72, 0.95), border: '1px solid #E5E7EB', padding: `${scaledBookingRem(0.62, 0.85)} ${scaledBookingRem(0.65, 0.95)}` }}>
                    <div style={{ fontSize: scaledBookingRem(0.58, 0.66), fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6B7280' }}>
                      Location
                    </div>
                    <div style={{ marginTop: scaledBookingRem(0.22, 0.3), fontSize: scaledBookingRem(0.7, 0.8), color: '#111827', lineHeight: 1.55 }}>
                      {businessAddress}
                    </div>
                  </div>
                ) : null}
                {appointmentInstructions ? (
                  <div style={{ borderRadius: scaledBookingRem(0.72, 0.95), border: '1px solid #E5E7EB', padding: `${scaledBookingRem(0.62, 0.85)} ${scaledBookingRem(0.65, 0.95)}` }}>
                    <div style={{ fontSize: scaledBookingRem(0.58, 0.66), fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6B7280' }}>
                      Appointment instructions
                    </div>
                    <div style={{ marginTop: scaledBookingRem(0.22, 0.3), fontSize: scaledBookingRem(0.7, 0.8), color: '#111827', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
                      {appointmentInstructions}
                    </div>
                  </div>
                ) : null}
              </div>

              <div style={{ marginTop: scaledBookingRem(0.8, 1.1), display: 'flex', gap: scaledBookingRem(0.5, 0.7), flexWrap: 'wrap', justifyContent: 'center' }}>
                {!previewMode && googleCalendarUrl ? (
                  <a
                    href={googleCalendarUrl}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      background: '#FFFFFF',
                      color: activeColorScheme.PRIMARY,
                      border: `1px solid ${activeColorScheme.PRIMARY}`,
                      padding: `${scaledBookingRem(0.6, 0.72)} ${scaledBookingRem(0.8, 1.1)}`,
                      borderRadius: scaledBookingRem(0.52, 0.65),
                      fontSize: scaledBookingRem(0.7, 0.78),
                      fontWeight: 800,
                      textDecoration: 'none',
                    }}
                  >
                    Add to Google Calendar
                  </a>
                ) : null}
                <button
                  type="button"
                  onClick={resetBookingFlow}
                  style={{
                    background: activeColorScheme.PRIMARY,
                    color: '#FFF',
                    border: 'none',
                    padding: `${scaledBookingRem(0.6, 0.72)} ${scaledBookingRem(0.82, 1.15)}`,
                    borderRadius: scaledBookingRem(0.52, 0.65),
                    fontSize: scaledBookingRem(0.7, 0.78),
                    fontWeight: 800,
                    cursor: 'pointer',
                  }}
                >
                  {previewMode ? 'Start Over' : confirmationMessage ? 'Back to Booking' : 'Book Another'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export { BookingPage };
export default BookingPage;
